import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowLeftRight, ArrowUp, ChevronDown, ChevronRight, ChevronUp, ClipboardPaste, Filter, Copy, Paperclip, Trash2, Upload, X } from 'lucide-react'
import {
  BOM_FILTER_KEYS,
  BOM_FILTER_NONE,
  MATERIAL_CERTIFICATE_TYPES,
  MATERIAL_CERTIFICATE_TYPE_LABELS,
  PROCUREMENT_SOURCE_LABELS,
  PROCUREMENT_LOCKED,
  PROCUREMENT_STATUS_LABELS,
  bomEinkaufSoll,
  bomZeilePasst,
  erfuelltRecht,
  type BomFilter,
  type BomFilterKey,
  type MaterialCertificateType,
  type ProcurementApprovalMode,
  type ProcurementSource,
} from '@symphenia/shared-types'
import { KlappUmschalter, useKlappModus, zeileOffen, type KlappModus } from '@/components/klappmodus'
import { FilterBar } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { ExportButton } from '@/components/ui/ExportButton'
import { ZiehBereich, useRandMarkieren, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { verschiebeMehrereVor } from '@/components/ziehen/ziehen'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { api } from '@/lib/axios'
import {
  eingabeOffen,
  gleich,
  mitStand,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Feld as SpeicherFeld,
  type Speicherstelle,
} from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import { fetchClipboard, uploadFile } from '../api/projects-phase-d.api'
import { createCertificate, oeffneZeugnis } from '../api/project-tech.api'
import {
  approveBomLines,
  assignCertificate,
  bomExportRequest,
  cancelBomLines,
  certificateCandidates,
  copyBomLines,
  createBomLine,
  deleteBomLine,
  fetchBom,
  fetchBomCategories,
  fetchBomTrash,
  fetchUnits,
  convertBomLine,
  pasteIntoBom,
  receiveBomLines,
  reorderBom,
  restoreBomLine,
  revokeBomApproval,
  searchSuppliers,
  suggestBom,
  unassignCertificate,
  type BomCategory,
  type BomLine,
  type BomLineInput,
  type BomState,
  type BomSuggestion,
  type CertificateCandidate,
} from '../api/project-bom.api'
import { alsTag } from '../project-labels'
import { AngeboteFenster } from './AngeboteFenster'
import { BomImportDialog } from './BomImportDialog'
import { EinkaufsKontext, EinkaufsMarke, type EinkaufsAktionen } from './EinkaufsMarke'
import { ApplyOffersDialog } from './OfferComparisonTab'
import { PapierkorbFenster, RueckgaengigHinweis } from './Papierkorb'
import { ProjectFilesPanel } from './ProjectFilesPanel'
import {
  Feld,
  RasterFeld,
  RasterInfo,
  Vorschlaege,
  ZeilenBlock,
  ZeilenListe,
  geldText,
  useListenBreite,
  useSeiteDaneben,
  useSuche,
  zahlText,
  zelle,
  type Seite,
  type Spalte,
} from './zeilenblock'

const zahl = (n: number | null | undefined, stellen = 3) =>
  n === null || n === undefined ? '—' : n.toLocaleString(navigator.language, { maximumFractionDigits: stellen })

const STATUS_FARBE: Record<string, string> = {
  OPEN: 'bg-muted text-muted-foreground',
  INQUIRED: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  ORDERED: 'bg-primary/15 text-primary',
  // AP-39 11.4: vom Lieferanten bestätigt.
  CONFIRMED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  RECEIVED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  STOCK: 'bg-muted text-foreground',
  PROVIDED: 'bg-muted text-foreground',
  CANCELLED: 'bg-muted text-muted-foreground line-through',
}

type Einheit = { id: string; code: string }
type Quelle = 'ARTICLES' | 'HISTORY'

/**
 * Ab dieser **Listenbreite** sind Status, Freigaben, Zeugnis und Dateien Spalten der ersten Zeile
 * (AP-35 Nachtrag 15, N15-8). Darunter bleibt die Anordnung der Felder aus Nachtrag 5a.
 */
const BREIT_AB = 1390

/**
 * Der Block mit Status, Freigaben und Dateien (AP-35 Nachtrag 5a) — seit Nachtrag 15 (N15-14) nur noch in der
 * schmalen Anordnung, und dort immer als eine Zeile **unter** den Feldern, nie daneben.
 */
const SEITE: Seite = { breite: 'w-[10.5rem]', min: 168, titel: 'Status · Freigaben · Dateien', nurDarunter: true }

const knopf = 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted'

// ── Filter (Nachtrag 6) ────────────────────────────────────────────────────

const MEHRFACH = ['is', 'is_not'] as FilterFieldDef['operators']
const JA_NEIN = [
  { value: 'yes', label: 'erteilt' },
  { value: 'no', label: 'fehlt' },
]
const BOM_FILTER_LAYOUT = 'project-bom'
/** Ob die Filterleiste offen ist — je Browser gemerkt, eine reine Bequemlichkeit. */
const FILTER_OFFEN = 'symphenia-bom-filter-offen'

/**
 * Die Werte der Filterleiste als `BomFilter` — derselbe, den der Export
 * bekommt. Die Gruppierung ist Freitext und darf ein Komma enthalten; die
 * Mehrfachauswahl trennt aber mit Komma, deshalb steht sie kodiert im Wert.
 */
function alsBomFilter(werte: Record<string, ActiveFilter>): BomFilter {
  const f: BomFilter = {}
  for (const key of BOM_FILTER_KEYS) {
    const a = werte[key]
    if (!a?.value) continue
    if (key === 'certificateMissing') {
      f[key] = { values: [a.value === 'true' ? 'yes' : 'no'] }
      continue
    }
    const values = a.value
      .split(',')
      .filter(Boolean)
      .map((v) => (key === 'grouping' ? decodeURIComponent(v) : v))
    if (values.length) f[key] = { values, ...(a.operator === 'is_not' ? { not: true } : {}) }
  }
  return f
}

/**
 * AP-35 Detailblatt 5 8.1 — **Reiter Stückliste an der Position.**
 *
 * Erfasst und geändert wird im Zeilenblock (`zeilenblock.tsx`, Auftraggeber
 * 18.9.2026): Jede Zeile ist direkt bearbeitbar, unten steht immer eine leere.
 *
 * Anordnung nach Nachtrag 5a (19.9.2026, am Entwurf mit dem Auftraggeber
 * abgestimmt): Zeile 1 trägt, was man beim Erfassen braucht; Zeile 2 bis 4
 * stehen spaltengenau darunter und erscheinen, solange die Zeile bearbeitet
 * wird. Rechts steht über die ganze Höhe ein Block mit Status, Freigaben und
 * Dateien; Zeugnisse und Belege öffnen sich über die Zeugnis-Marke.
 *
 * Nachtrag 15 (11.10.2026, am Nachbau abgenommen): Die Liste hat die ganze
 * Breite des Reiters und teilt sich nach ihrer **eigenen** Breite neu auf. Ab
 * 1.390 px sind Werkstoff, Status, Freigaben, Zeugnis und Dateien Spalten der
 * ersten Zeile, der Block rechts entfällt, und aufgeklappt stehen zwei Zeilen
 * statt drei (N15-8 bis N15-13). Darunter bleiben die Felder wie in Nachtrag 5a;
 * der Block steht dann immer als eine Zeile unter ihnen (N15-14). Die Freigaben
 * sind in beiden Anordnungen immer zu sehen (N15-9), die Tab-Kette bleibt (N15-19).
 *
 * Nachtrag 6 (19.9.2026): Filterleiste, Export (auch per Mail), Ziehen,
 * Dateien per Drag & Drop auf die Zeile, Löschen mit Rückgängig über den
 * Papierkorb. Seit AP-32 N-64 neu (19.9.2026) greift man die Zeile an ihrer
 * freien Fläche und markiert am linken Rand; markierte Zeilen wandern
 * gemeinsam, ein Klick anderswohin hebt die Markierung auf.
 *
 * Anfrage und Bestellung entstehen **nicht** hier: markieren, kopieren, im Beleg
 * einfügen — oder im Einkaufs-Cockpit (5.).
 */
export function PositionBomTab({ projectId, positionId }: { projectId: string; positionId: string }) {
  const { user } = useAuthStore()
  const { t: uebersetze } = useTranslation()
  const darf = (r: string) => erfuelltRecht(r, user?.permissions)
  const darfPflegen = darf('projects.bom.manage')
  const darfFreigeben = darf('projects.approve.procurement')
  const darfZeugnis = darf('projects.certificate.manage')
  const darfDateien = darf('projects.position.manage')
  const darfExport = darf('projects.export.run')
  const preise = darf('projects.price.view')
  const navigate = useNavigate()

  const [daten, setDaten] = useState<BomState | null>(null)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [filterWerte, setFilterWerte] = useState<Record<string, ActiveFilter>>({})
  // Auftraggeber 10.10.2026: Zeilen immer aufgeklappt, immer zugeklappt oder beim Bearbeiten — je Benutzer gemerkt.
  const [klappModus, setKlappModus] = useKlappModus('project_bom')
  const [filterOffen, setFilterOffen] = useState(() => {
    try {
      return localStorage.getItem(FILTER_OFFEN) === '1'
    } catch {
      return false
    }
  })
  const schalteFilter = (offen: boolean) => {
    setFilterOffen(offen)
    try {
      localStorage.setItem(FILTER_OFFEN, offen ? '1' : '0')
    } catch {
      /* nur Bequemlichkeit */
    }
  }
  const [quelle, setQuelle] = useState<Quelle>('ARTICLES')
  const [importOffen, setImportOffen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  /** Zuletzt gelöscht — „Rückgängig“ holt den jüngsten zurück. */
  const [geloescht, setGeloescht] = useState<{ trashId: string; name: string }[]>([])
  const [papierkorbOffen, setPapierkorbOffen] = useState(false)
  const [kategorien, setKategorien] = useState<BomCategory[]>([])
  const [einheiten, setEinheiten] = useState<Einheit[]>([])
  const [neuSchluessel, setNeuSchluessel] = useState(0)
  const [neuFokus, setNeuFokus] = useState(false)
  // AP-35 N17-1, N17-2: das beste Angebot einer Zeile übernehmen bzw. alle Angebote im Fenster vergleichen.
  const [vorschlag, setVorschlag] = useState<{ choices: { lineId: string; documentLineId: string }[]; stands: { lineId: string; updatedAt: string }[] } | null>(null)
  const [angeboteOffen, setAngeboteOffen] = useState(false)
  const darfUebernehmen = darfPflegen && preise
  const einkauf = useMemo<EinkaufsAktionen>(
    () => ({
      uebernehmen: darfUebernehmen
        ? (z) => {
            if (!z.offer?.best) return
            setVorschlag({ choices: [{ lineId: z.id, documentLineId: z.offer.best.documentLineId }], stands: z.updatedAt ? [{ lineId: z.id, updatedAt: z.updatedAt }] : [] })
          }
        : undefined,
      vergleichen: darfUebernehmen ? () => setAngeboteOffen(true) : undefined,
      oeffneBeleg: (id) => navigate(`/correspondence/${id}`),
    }),
    [darfUebernehmen, navigate],
  )
  const behaelter = useRef<HTMLDivElement>(null)
  // Nachtrag 15: Die Anordnung folgt der Breite der Liste, nicht der des Fensters. Gemessen wird am äußersten
  // Element — es steht schon beim Laden da; ein später eingehängtes sähe der Beobachter nie.
  const liste = useListenBreite()
  const breit = liste.breite >= BREIT_AB

  const laden = useCallback(async () => {
    try {
      setDaten(await fetchBom(projectId, positionId))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Stückliste konnte nicht geladen werden.'))
    }
  }, [projectId, positionId])

  useEffect(() => {
    void laden()
    setGeloescht([])
    fetchBomCategories().then(setKategorien).catch(() => undefined)
    fetchUnits().then(setEinheiten).catch(() => undefined)
  }, [laden])

  // Ohne Preisrecht bleiben die Preisspalten als Lücke stehen: Nur ihr Inhalt
  // fehlt, alle anderen Felder behalten ihren Platz (Auftraggeber 19.9.2026).
  const spalten = useMemo<Spalte[]>(
    () =>
      breit
        ? [
            // Nachtrag 15 (N15-8): Werkstoff, Status, Freigaben, Zeugnis und Dateien sind Spalten; rechts die Aktionen.
            { key: 'menge', label: 'Menge', spur: '3.5rem', min: 56, rechts: true },
            { key: 'einheit', label: 'Einheit', spur: '3.5rem', min: 56 },
            { key: 'name', label: 'Bezeichnung', spur: 'minmax(10rem,2fr)', min: 160 },
            { key: 'werkstoff', label: 'Werkstoff', spur: '6rem', min: 96 },
            { key: 'kategorie', label: 'Kategorie', spur: '6rem', min: 96 },
            { key: 'mengenbezug', label: 'Mengenbezug', spur: '5.5rem', min: 88 },
            { key: 'ep', label: preise ? 'EP' : '', spur: '4rem', min: 64, rechts: true },
            { key: 'pe', label: preise ? 'PE' : '', spur: '3.5rem', min: 56, rechts: true },
            { key: 'rabatt', label: preise ? 'Rab.' : '', spur: '2.5rem', min: 40, rechts: true },
            { key: 'gp', label: preise ? 'GP' : '', spur: '4.75rem', min: 76, rechts: true },
            { key: 'status', label: 'Status', spur: '6.5rem', min: 104 },
            { key: 'freigaben', label: daten?.approvalMode === 'NONE' ? '' : 'Freigaben', spur: '8.75rem', min: 140 },
            { key: 'zeugnis', label: 'Zeugnis', spur: '3.75rem', min: 60 },
            { key: 'dateien', label: 'Dateien', spur: '3.25rem', min: 52 },
            { key: 'aktionen', label: '', spur: '2.75rem', min: 44 },
          ]
        : [
            { key: 'menge', label: 'Menge', spur: '3.5rem', min: 56, schmal: 'w-20', rechts: true },
            { key: 'einheit', label: 'Einheit', spur: '3.5rem', min: 56, schmal: 'w-20' },
            { key: 'name', label: 'Bezeichnung', spur: 'minmax(7.5rem,2fr)', min: 120, schmal: 'w-72' },
            { key: 'kategorie', label: 'Kategorie', spur: '6rem', min: 96, schmal: 'w-36' },
            { key: 'mengenbezug', label: 'Mengenbezug', spur: '5.5rem', min: 88, schmal: 'w-28' },
            { key: 'ep', label: preise ? 'EP' : '', spur: '4rem', min: 64, schmal: 'w-24', rechts: true },
            { key: 'pe', label: preise ? 'PE' : '', spur: '3.5rem', min: 56, schmal: 'w-16', rechts: true },
            { key: 'rabatt', label: preise ? 'Rab.' : '', spur: '2.25rem', min: 36, schmal: 'w-12', rechts: true },
            { key: 'gp', label: preise ? 'GP' : '', spur: '4.5rem', min: 72, schmal: 'w-24', rechts: true },
          ],
    [preise, breit, daten?.approvalMode],
  )

  // Nachtrag 15 (N15-15): Das Feld „Gruppierung" schlägt vor, was in den Zeilen dieser Position schon steht —
  // aus allen Zeilen, nicht nur den gefilterten.
  const gruppierungen = useMemo(
    () => [...new Set((daten?.lines ?? []).map((l) => l.grouping?.trim() ?? '').filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [daten],
  )

  // Die Auswahl der Filterfelder entsteht aus dem, was in der Liste vorkommt —
  // Kategorien und Status vollständig, damit auch „keine Zeile“ eine Antwort ist.
  const filterFelder = useMemo<FilterFieldDef[]>(() => {
    const alle = daten?.lines ?? []
    const eindeutig = (paare: [string, string][]) => [...new Map(paare).entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
    const ohne = (label: string) => ({ value: BOM_FILTER_NONE, label })
    const gruppen = eindeutig(alle.filter((l) => l.grouping?.trim()).map((l) => [encodeURIComponent(l.grouping!.trim()), l.grouping!.trim()]))
    const modus = daten?.approvalMode ?? 'ORDER_ONLY'
    const felder: FilterFieldDef[] = [
      {
        key: 'supplier',
        label: 'Lieferant',
        type: 'select-multi',
        category: 'core',
        operators: MEHRFACH,
        options: [...eindeutig(alle.filter((l) => l.supplier).map((l) => [l.supplier!.id, l.supplier!.name])), ohne('ohne Lieferant')],
      },
      {
        key: 'order',
        label: 'Bestellung',
        type: 'select-multi',
        category: 'core',
        operators: MEHRFACH,
        options: [...eindeutig(alle.filter((l) => l.orderDocument).map((l) => [l.orderDocument!.id, l.orderDocument!.number ?? 'Entwurf'])), ohne('noch nicht bestellt')],
      },
      {
        key: 'category',
        label: 'Kategorie',
        type: 'select-multi',
        category: 'core',
        operators: MEHRFACH,
        options: [...kategorien.map((k) => ({ value: k.id, label: k.name })), ohne('ohne Kategorie')],
      },
      {
        key: 'source',
        label: 'Bezug der Ware',
        type: 'select-multi',
        category: 'core',
        operators: MEHRFACH,
        options: Object.entries(PROCUREMENT_SOURCE_LABELS).map(([value, label]) => ({ value, label })),
      },
      { key: 'grouping', label: 'Gruppierung', type: 'select-multi', category: 'core', operators: MEHRFACH, options: [...gruppen, ohne('ohne Gruppierung')] },
      {
        key: 'status',
        label: 'Status',
        type: 'select-multi',
        category: 'core',
        operators: MEHRFACH,
        options: Object.entries(PROCUREMENT_STATUS_LABELS).map(([value, label]) => ({ value, label })),
      },
    ]
    if (modus === 'INQUIRY_AND_ORDER') felder.push({ key: 'inquiryApproval', label: 'Anfragefreigabe', type: 'select', category: 'core', operators: ['is'], options: JA_NEIN })
    if (modus !== 'NONE') felder.push({ key: 'orderApproval', label: 'Bestellfreigabe', type: 'select', category: 'core', operators: ['is'], options: JA_NEIN })
    felder.push({ key: 'certificateMissing', label: 'Werkszeugnis fehlt', type: 'boolean', category: 'core', operators: ['is'] })
    return felder
  }, [daten, kategorien])

  const bomFilter = useMemo(() => alsBomFilter(filterWerte), [filterWerte])
  const gefiltert = Object.keys(bomFilter).length > 0
  const filterAnzahl = Object.keys(bomFilter).length
  const zeilen = useMemo(() => (daten?.lines ?? []).filter((l) => bomZeilePasst(l, bomFilter)), [daten, bomFilter])
  const auswahl = [...markiert]

  /** Die gesetzten Filter als Textzeile über der Tabelle im Export. */
  const filterText = () =>
    (Object.entries(bomFilter) as [BomFilterKey, { values: string[]; not?: boolean }][])
      .map(([key, f]) => {
        const feld = filterFelder.find((x) => x.key === key)
        if (key === 'certificateMissing') return f.values[0] === 'yes' ? 'Werkszeugnis fehlt' : 'Werkszeugnis vorhanden oder nicht nötig'
        const namen = f.values.map((v) => feld?.options?.find((o) => o.value === (key === 'grouping' && v !== BOM_FILTER_NONE ? encodeURIComponent(v) : v))?.label ?? v)
        return `${feld?.label ?? key}${f.not ? ' nicht' : ''}: ${namen.join(', ')}`
      })
      .join(' · ')

  async function tu(aktion: () => Promise<unknown>, erfolg?: string) {
    setFehler(null)
    setHinweis(null)
    try {
      await aktion()
      if (erfolg) setHinweis(erfolg)
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  /** Neue Reihenfolge an den Server; bis zur Antwort steht sie schon so da. */
  async function ordne(ids: readonly string[]) {
    if (!daten) return
    const nach = new Map(daten.lines.map((l) => [l.id, l]))
    setDaten({ ...daten, lines: ids.map((id) => nach.get(id)!).filter(Boolean) })
    try {
      setDaten(await reorderBom(projectId, positionId, [...ids]))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Reihenfolge ließ sich nicht speichern.'))
      await laden()
    }
  }

  async function schiebe(richtung: -1 | 1) {
    if (!daten || !auswahl.length) return
    const ids = daten.lines.map((l) => l.id)
    const reihe = richtung < 0 ? ids : [...ids].reverse()
    for (let i = 1; i < reihe.length; i++) {
      if (auswahl.includes(reihe[i]) && !auswahl.includes(reihe[i - 1])) [reihe[i - 1], reihe[i]] = [reihe[i], reihe[i - 1]]
    }
    setHinweis(null)
    await ordne(richtung < 0 ? reihe : [...reihe].reverse())
  }

  /** Mit der gegriffenen wandern alle markierten sichtbaren Zeilen — ist sie selbst markiert (N-69 neu). */
  const mitGezogen = (key: string) => (markiert.has(key) ? zeilen.filter((z) => markiert.has(z.id)).map((z) => z.id) : [key])

  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      if (!daten) return
      const ids = daten.lines.map((l) => l.id)
      // Gefiltert: „ans Ende“ heißt hinter die letzte **sichtbare** Zeile, nicht ans Ende der ganzen Liste.
      let vor = vorKey
      if (vor === null && gefiltert && zeilen.length) {
        const letzte = ids.indexOf(zeilen[zeilen.length - 1].id)
        vor = ids[letzte + 1] ?? null
      }
      const neu = verschiebeMehrereVor(ids, mitGezogen(key), vor)
      if (neu !== ids) void ordne(neu)
    },
  })

  const reihe = useMemo(() => zeilen.map((z) => z.id), [zeilen])
  const { amRand } = useRandMarkieren({
    behaelter,
    reihe,
    markiert,
    setMarkiert,
    onKopieren: () => void tu(() => copyBomLines([...markiert]), 'In die Zwischenablage kopiert — im Beleg einfügen.'),
  })

  async function ausAblage() {
    const eintraege = (await fetchClipboard()).filter((e) => (e.position as { lineType?: string }).lineType === 'ARTICLE')
    if (!eintraege.length) {
      setHinweis('Die Zwischenablage hat keine Zeilen, die in eine Stückliste passen.')
      return
    }
    await tu(async () => setDaten(await pasteIntoBom(projectId, positionId, eintraege.map((e) => e.id))), `${eintraege.length} Zeilen eingefügt.`)
  }

  /** Löschen ohne Rückfrage — es lässt sich zurücknehmen (Papierkorb, 30 Tage). */
  async function loeschen(z: BomLine) {
    setFehler(null)
    setHinweis(null)
    try {
      const r = await deleteBomLine(z.id)
      setGeloescht((g) => [...g, r])
      setMarkiert((m) => {
        const n = new Set(m)
        n.delete(z.id)
        return n
      })
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Zeile ließ sich nicht löschen.'))
    }
  }

  async function wiederherstellen(trashId: string) {
    setFehler(null)
    setHinweis(null)
    try {
      setDaten(await restoreBomLine(trashId))
      setGeloescht((g) => g.filter((x) => x.trashId !== trashId))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Zeile ließ sich nicht wiederherstellen.'))
    }
  }

  async function dateienAnhaengen(z: BomLine, dateien: File[]) {
    setFehler(null)
    setHinweis(null)
    let ok = 0
    const fehlgeschlagen: string[] = []
    for (const d of dateien) {
      try {
        await uploadFile(projectId, d, { entityType: 'BOM_LINE', entityId: z.id })
        ok++
      } catch (e) {
        fehlgeschlagen.push(`${d.name}: ${apiErrorText(e, 'nicht hochgeladen')}`)
      }
    }
    if (ok) setHinweis(`${ok} ${ok === 1 ? 'Datei' : 'Dateien'} an „${z.name}“ gehängt.`)
    if (fehlgeschlagen.length) setFehler(fehlgeschlagen.join(' · '))
    await laden()
  }

  // Dasselbe äußere Element wie unten: An ihm hängt die Messung der Listenbreite.
  if (!daten) return <div ref={liste.ref}>{fehler ? <p className="text-sm text-destructive">{fehler}</p> : <p className="text-sm text-muted-foreground">Lädt …</p>}</div>

  const t = daten.totals
  const gezogeneZeile = gezogen ? daten.lines.find((l) => l.id === gezogen) : undefined
  const gezogeneIds = new Set(gezogen ? mitGezogen(gezogen) : [])
  const gemeinsam = {
    projectId,
    positionId,
    kategorien,
    einheiten,
    preise,
    darfPflegen,
    darfFreigeben,
    darfZeugnis,
    quelle,
    approvalMode: daten.approvalMode,
    vorgabeBezug: daten.quantityBasisDefault ?? 'TOTAL',
    positionsMenge: daten.positionQuantity,
    breit,
    klappModus,
    gruppierungen,
  }
  return (
    // Das äußere Element bleibt das `div` mit der Messung (wie beim Laden oben): Stünde der Kontext außen, ersetzte React
    // das gemessene Element, sobald die Daten da sind — und die Breite bliebe beim Wert des Ladens stehen (Nachtrag 15).
    <div ref={liste.ref} className="space-y-3">
      <EinkaufsKontext.Provider value={einkauf}>
        {/* Bleibt eingehängt, auch eingeklappt: Sie stellt beim Laden die zuletzt
            benutzten Filter wieder her, und der Knopf zählt sie. */}
        <div className={filterOffen ? '' : 'hidden'}>
          <FilterBar
            availableFields={filterFelder}
            value={filterWerte}
            onChange={setFilterWerte}
            layoutKey={BOM_FILTER_LAYOUT}
            defaultFieldKeys={BOM_FILTER_KEYS.slice()}
            festeFelder
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-expanded={filterOffen}
            aria-label={filterAnzahl ? `Filter — ${filterAnzahl} aktiv` : 'Filter'}
            title={filterOffen ? 'Filterleiste ausblenden' : 'Filterleiste einblenden'}
            className={`${knopf} ${filterAnzahl ? 'border-primary bg-primary/10 font-medium text-primary hover:bg-primary/15' : ''} ${filterOffen && !filterAnzahl ? 'bg-muted' : ''}`}
            onClick={() => schalteFilter(!filterOffen)}
          >
            <Filter size={14} className={filterAnzahl ? 'fill-current' : ''} /> Filter
            {filterAnzahl > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] leading-4 text-primary-foreground">{filterAnzahl}</span>}
            {filterOffen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          <KlappUmschalter modus={klappModus} onModus={setKlappModus} className="self-stretch rounded-md" />
          {darfPflegen && (
            <>
              <button type="button" className={knopf} onClick={() => setImportOffen(true)}>
                <Upload size={14} /> Import
              </button>
              <button type="button" className={knopf} onClick={() => void ausAblage()}>
                <ClipboardPaste size={14} /> Aus Ablage
              </button>
              <div className="flex overflow-hidden rounded-md border border-border text-xs" role="group" aria-label="Vorschläge aus">
                {(['ARTICLES', 'HISTORY'] as const).map((q) => (
                  <button key={q} type="button" onClick={() => setQuelle(q)} className={`px-2 py-1.5 ${quelle === q ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                    {q === 'ARTICLES' ? 'Vorschläge: Artikelstamm' : 'bisherige Eingaben'}
                  </button>
                ))}
              </div>
            </>
          )}
          {auswahl.length > 0 ? (
            <div className="ml-auto flex flex-wrap items-center gap-1 text-sm" data-auswahl-behalten>
              <span className="mr-1 text-muted-foreground">{auswahl.length} markiert</span>
              <button type="button" className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => copyBomLines(auswahl), 'In die Zwischenablage kopiert — im Beleg einfügen.')}>
                <Copy size={13} /> Kopieren
              </button>
              {darfFreigeben && (
                <>
                  <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => approveBomLines(auswahl, 'ORDER'), 'Bestellfreigabe erteilt.')}>
                    Bestellfreigabe
                  </button>
                  {daten.approvalMode === 'INQUIRY_AND_ORDER' && (
                    <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => approveBomLines(auswahl, 'INQUIRY'), 'Anfragefreigabe erteilt.')}>
                      Anfragefreigabe
                    </button>
                  )}
                </>
              )}
              {darfPflegen && (
                <>
                  <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => receiveBomLines(auswahl), 'Als erhalten vermerkt.')}>
                    erhalten
                  </button>
                  <button type="button" className="rounded border border-border px-2 py-1 text-destructive hover:bg-muted" onClick={() => void tu(() => cancelBomLines(auswahl), 'Storniert.')}>
                    stornieren
                  </button>
                  <button type="button" className="rounded border border-border p-1 hover:bg-muted" aria-label="nach oben" onClick={() => void schiebe(-1)}>
                    <ArrowUp size={14} />
                  </button>
                  <button type="button" className="rounded border border-border p-1 hover:bg-muted" aria-label="nach unten" onClick={() => void schiebe(1)}>
                    <ArrowDown size={14} />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="ml-auto flex items-center gap-2">
              {darfPflegen && (
                <div className="relative">
                  <button type="button" className={knopf} aria-expanded={papierkorbOffen} onClick={() => setPapierkorbOffen((x) => !x)}>
                    <Trash2 size={14} /> Papierkorb
                  </button>
                  {papierkorbOffen && (
                    <PapierkorbFenster
                      titel="Papierkorb der Stückliste"
                      laden={() => fetchBomTrash(projectId, positionId)}
                      onWiederherstellen={(id) => void wiederherstellen(id)}
                      onSchliessen={() => setPapierkorbOffen(false)}
                    />
                  )}
                </div>
              )}
              {darfExport && (
                <ExportButton
                  request={(format, orientation) => bomExportRequest(projectId, positionId, { format, orientation, filter: bomFilter, filterInfo: filterText() || undefined })}
                  mail={{
                    source: 'EXPORT_PROJECT_BOM',
                    title: uebersetze('mailExport.titles.EXPORT_PROJECT_BOM'),
                    subject: `${uebersetze('mailExport.titles.EXPORT_PROJECT_BOM')} ${daten.lines[0]?.project.projectNumber ?? ''} · ${daten.lines[0]?.position.label ?? ''}`.trim(),
                  }}
                />
              )}
            </div>
          )}
        </div>

        <EinkaufsHinweise lines={daten.lines} darfUebernehmen={darfUebernehmen} darfEinkauf={darfPflegen} onAngebote={() => setAngeboteOffen(true)} />
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}
        <RueckgaengigHinweis geloescht={geloescht} onRueckgaengig={(id) => void wiederherstellen(id)} onSchliessen={() => setGeloescht([])} />
        {gefiltert && (
          <p className="text-xs text-muted-foreground">
            {zeilen.length} von {daten.lines.length} Zeilen gefiltert{darfPflegen ? ' — neue Zeilen erscheinen nur, wenn sie zum Filter passen.' : '.'}{' '}
            <button type="button" className="text-primary hover:underline" onClick={() => setFilterWerte({})}>
              Filter zurücksetzen
            </button>
          </p>
        )}

        <div ref={behaelter}>
          <ZiehBereich
            dndProps={dndProps}
            vorschau={
              gezogeneZeile && (
                <>
                  {gezogeneIds.size > 1 && <span className="mr-1 rounded bg-primary px-1.5 text-xs text-primary-foreground">{gezogeneIds.size} Zeilen</span>}
                  <span className="tabular-nums text-muted-foreground">
                    {zahl(gezogeneZeile.quantity)} {gezogeneZeile.unit?.code}
                  </span>{' '}
                  <span className="font-medium">{gezogeneZeile.name}</span>
                </>
              )
            }
          >
            <ZeilenListe
              spalten={spalten}
              seite={breit ? undefined : SEITE}
              abgesetzt
              kopfJeZeile
              klappModus={klappModus}
              fuss={
                <div className="flex flex-wrap justify-between gap-2 border-t-2 border-border bg-muted/40 px-3 py-1.5 text-xs font-medium">
                  <span>
                    {t.count} Zeilen
                    {t.certificatesMissing > 0 && <span className="ml-3 text-destructive">{t.certificatesMissing} ohne gültiges Zeugnis</span>}
                    {t.requirementIncreases > 0 && <span className="ml-3 text-amber-700 dark:text-amber-400">{t.requirementIncreases} mit höherem Bedarf als bestellt</span>}
                  </span>
                  <span className="tabular-nums">
                    {zahl(t.weight, 1)} kg{t.weightUnknown > 0 ? ` (+${t.weightUnknown} ohne Gewicht)` : ''}
                    {preise && ` · EK gesamt ${geldText(t.purchaseTotal)}`}
                  </span>
                </div>
              }
            >
              {zeilen.length === 0 && !darfPflegen && <p className="px-3 py-6 text-center text-sm text-muted-foreground">{gefiltert ? 'Keine Zeile passt zum Filter.' : 'Noch keine Zeilen.'}</p>}
              {zeilen.map((z) => (
                <StuecklistenZeile
                  key={z.id}
                  z={z}
                  {...gemeinsam}
                  markiert={markiert.has(z.id)}
                  ziehbar={darfPflegen}
                  gezogen={gezogeneIds.has(z.id)}
                  ziellinie={ziel?.key === z.id ? ziel.kante : null}
                  onRand={amRand(z.id)}
                  onGeaendert={laden}
                  onLoeschen={() => void loeschen(z)}
                  onDateien={darfDateien ? (d) => void dateienAnhaengen(z, d) : undefined}
                  onOpenDocument={(id) => navigate(`/correspondence/${id}`)}
                />
              ))}
              {darfPflegen && (
                <StuecklistenZeile
                  key={`neu-${neuSchluessel}`}
                  {...gemeinsam}
                  autoFokus={neuFokus}
                  onAnlegen={async (dto, perTastatur, danach) => {
                    const r = await createBomLine(projectId, positionId, dto)
                    setHinweis(r.hints.includes('projects.hints.bomArticleNotPurchase') ? 'Hinweis: Der Artikel ist kein Einkaufsartikel.' : null)
                    try {
                      if (danach) await danach(r.data)
                    } finally {
                      await laden()
                      setNeuFokus(perTastatur)
                      setNeuSchluessel((k) => k + 1)
                    }
                  }}
                  onGeaendert={laden}
                />
              )}
            </ZeilenListe>
          </ZiehBereich>
        </div>

        {importOffen && (
          <BomImportDialog
            projectId={projectId}
            positionId={positionId}
            onClose={() => setImportOffen(false)}
            onDone={async (text) => {
              setImportOffen(false)
              setHinweis(text)
              await laden()
            }}
          />
        )}
        {vorschlag && (
          <ApplyOffersDialog
            choices={vorschlag.choices}
            stands={vorschlag.stands}
            onClose={() => setVorschlag(null)}
            onFertig={(r) => {
              setVorschlag(null)
              setHinweis(r.applied ? `${r.applied === 1 ? '1 Preis' : `${r.applied} Preise`} aus dem Angebot übernommen.` : 'Nichts übernommen — die Zeile hat sich inzwischen geändert.')
              void laden()
            }}
          />
        )}
        {angeboteOffen && (
          <AngeboteFenster
            projectId={projectId}
            positionId={positionId}
            onClose={() => setAngeboteOffen(false)}
            onGeaendert={() => {
              setAngeboteOffen(false)
              setHinweis('Preise aus den Angeboten übernommen.')
              void laden()
            }}
          />
        )}
      </EinkaufsKontext.Provider>
    </div>
  )
}

/**
 * AP-35 N17-2, N17-10 — was der Einkauf an dieser Position zu tun hat: Angebote, die nicht übernommen sind, und
 * Bestellungen ohne oder mit abweichender Auftragsbestätigung. Der Verweis öffnet den Einkauf mit dem Projekt im Filter.
 */
function EinkaufsHinweise({ lines, darfUebernehmen, darfEinkauf, onAngebote }: { lines: BomLine[]; darfUebernehmen: boolean; darfEinkauf: boolean; onAngebote: () => void }) {
  const angebote = lines.filter((l) => l.offer?.state === 'OFFER').length
  const ohneAb = lines.filter((l) => l.confirmation?.state === 'MISSING' || l.confirmation?.state === 'READ').length
  const abweichend = lines.filter((l) => l.confirmation?.state === 'DEVIATION').length
  const projekt = lines[0]?.project.projectNumber
  if (!angebote && !ohneAb && !abweichend) return null
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs" data-einkaufshinweise>
      {angebote > 0 && (
        <span>
          <span className="font-medium text-emerald-700 dark:text-emerald-400">
            {angebote} {angebote === 1 ? 'Angebot' : 'Angebote'} nicht übernommen
          </span>
          {darfUebernehmen && (
            <>
              {' · '}
              <button type="button" className="text-primary hover:underline" onClick={onAngebote}>
                übernehmen
              </button>
            </>
          )}
        </span>
      )}
      {(ohneAb > 0 || abweichend > 0) && (
        <span>
          {ohneAb > 0 && <span className="text-amber-700 dark:text-amber-400">{ohneAb} ohne AB</span>}
          {ohneAb > 0 && abweichend > 0 && ' · '}
          {abweichend > 0 && <span className="font-medium text-destructive">{abweichend} mit Abweichung</span>}
          {darfEinkauf && projekt && (
            <>
              {' · '}
              <Link
                to={`/purchasing?tab=ordered&projectSearch=${encodeURIComponent(projekt)}&confirmation=${ohneAb > 0 ? 'MISSING' : 'DEVIATION'}`}
                className="text-primary hover:underline"
              >
                im Einkauf prüfen
              </Link>
            </>
          )}
        </span>
      )}
    </div>
  )
}

// ── Eine Zeile ─────────────────────────────────────────────────────────────

/**
 * Die Zeile als flacher Datensatz für die Speicherstelle: Das GET liefert die
 * Bezüge als Objekt (`unit.id`), der PATCH erwartet die Kennung (`unitId`). Die
 * Basis trägt sie deshalb in der Form, in der sie gesendet werden — sonst
 * verglichen die Felder gegen einen Wert, den der Patch gar nicht kennt.
 */
function alsDatensatz(zeile: Datensatz): Datensatz {
  const bezug = (feld: string) => (zeile[feld] as { id?: string } | null)?.id ?? null
  return {
    ...zeile,
    articleId: bezug('article'),
    categoryId: bezug('category'),
    unitId: bezug('unit'),
    altUnitId: bezug('altUnit'),
    supplierId: bezug('supplier'),
    // Eine ältere Zeile kann Zeugnispflicht ohne Art tragen; die Auswahl zeigt dann 3.1.
    certificateType: zeile.requiresCertificate ? (zeile.certificateType ?? 'EN10204_3_1') : null,
  }
}

/**
 * Die Vorgaben der leeren letzten Zeile. Auch sie hängt an einer Stelle, damit
 * ihre Felder dieselben sind wie überall — geschrieben wird dort aber nie:
 * Angelegt wird beim Verlassen der Zeile (Fall 2 „Anlegen").
 */
const NEUE_ZEILE: Datensatz = {
  quantityBasis: 'TOTAL',
  procurementSource: 'PURCHASE',
  priceUnit: 1,
  requiresCertificate: false,
  galvanizingSuitabilityRequired: false,
}

interface ZeilenProps {
  z?: BomLine
  /** Mengenbezug der leeren Zeile (Einstellungen). */
  vorgabeBezug?: 'TOTAL' | 'PER_UNIT'
  /** Menge der Position — der GP einer Zeile „je Einheit" rechnet damit, während getippt wird. */
  positionsMenge?: number
  projectId: string
  positionId: string
  kategorien: BomCategory[]
  einheiten: Einheit[]
  preise: boolean
  darfPflegen: boolean
  darfFreigeben: boolean
  darfZeugnis: boolean
  quelle: Quelle
  approvalMode: ProcurementApprovalMode
  /** Breite Anordnung (Nachtrag 15): Status, Freigaben, Zeugnis und Dateien als Spalten, zwei Aufklappzeilen. */
  breit: boolean
  /** Wie die Liste aufklappt — der Hinweis der schmalen Anordnung steht nur an der offenen Zeile (N15-14). */
  klappModus?: KlappModus
  /** Gruppierungen, die in den Zeilen dieser Position schon stehen — Vorschläge des Felds (N15-15). */
  gruppierungen: string[]
  markiert?: boolean
  autoFokus?: boolean
  /** An der freien Fläche greifbar (N-64 neu). */
  ziehbar?: boolean
  gezogen?: boolean
  ziellinie?: 'oben' | 'unten' | null
  /** Druck auf den linken Rand — markieren (N-64 neu). */
  onRand?: (e: ReactMouseEvent) => void
  /** Nur die leere letzte Zeile: anlegen beim Verlassen (Fall 2). */
  onAnlegen?: (dto: BomLineInput, perTastatur: boolean, danach?: (neu: BomLine) => Promise<unknown>) => Promise<void>
  onGeaendert: () => Promise<void>
  onLoeschen?: () => void
  /** Dateien per Drag & Drop auf die Zeile (Nachtrag 6). */
  onDateien?: (dateien: File[]) => void
  onOpenDocument?: (id: string) => void
}

/**
 * Tab-Reihenfolge (Auftraggeber 19.9.2026): Menge → Einheit → Bezeichnung →
 * Langtext → Kategorie → EP → PE → Rabatt → nächste Zeile. Die Nummern stehen
 * als `data-haupt` an den Feldern; der Zeilenblock springt danach. Nachtrag 15
 * (N15-19) lässt sie, wie sie ist: Die neuen Spalten der ersten Zeile liegen
 * außerhalb der Kette.
 */
const KETTE = { menge: 1, einheit: 2, name: 3, langtext: 4, kategorie: 5, ep: 6, pe: 7, rabatt: 8 } as const

function StuecklistenZeile({
  z,
  projectId,
  positionId,
  kategorien,
  einheiten,
  preise,
  darfPflegen,
  darfFreigeben,
  darfZeugnis,
  quelle,
  approvalMode,
  breit,
  klappModus,
  gruppierungen,
  vorgabeBezug = 'TOTAL',
  positionsMenge,
  markiert,
  autoFokus,
  ziehbar,
  gezogen,
  ziellinie,
  onRand,
  onAnlegen,
  onGeaendert,
  onLoeschen,
  onDateien,
  onOpenDocument,
}: ZeilenProps) {
  const terms = useTerminology()
  const [fehler, setFehler] = useState<string | null>(null)
  const [sucheName, setSucheName] = useState(false)
  const [sucheLieferant, setSucheLieferant] = useState(false)
  /** Vorschläge der Gruppierung: beim Fokus alle, beim Tippen die passenden (N15-15). */
  const [sucheGruppe, setSucheGruppe] = useState<'alle' | 'passende' | null>(null)
  /** Zeugnisse oder Dateien der Zeile — das Fenster, das gerade offen ist. */
  const [fenster, setFenster] = useState<Fenster>(null)
  const umschalten = (f: Exclude<Fenster, null>) => setFenster((x) => (x === f ? null : f))

  const zeilenId = z?.id
  const bestellt = Boolean(z && PROCUREMENT_LOCKED.includes(z.procurementStatus))
  const storniert = z?.procurementStatus === 'CANCELLED'
  const sperre = !darfPflegen || storniert
  const sperreBestellt = sperre || bestellt
  // AP-37 N3-8: in kg umgerechnet — die Einheit bleibt kg; Menge in kg und Alternativmenge rechnen einander um (N4-5).
  const umgerechnet = z?.altWeightPerUnit != null
  const [rechnet, setRechnet] = useState(false)
  // Die leere Zeile wird genau einmal angelegt (Umrechnen legt an, der Fokusverlust sonst ein zweites Mal).
  const legtAn = useRef(false)

  /**
   * Im Lieferantenfeld steht der Name, in der Zeile die Kennung — die Zuordnung
   * kennt nur die Oberfläche. Gemerkt wird, was Zeile und Suche gezeigt haben.
   */
  const lieferantenNamen = useRef(new Map<string, string>())
  if (z?.supplier) lieferantenNamen.current.set(z.supplier.id, z.supplier.name)
  const lieferantText = (id: string | null) => (id ? (lieferantenNamen.current.get(id) ?? '') : '')

  /**
   * **Eine Speicherstelle je Zeile** (AP-36 7.2). Die geänderten Felder gehen
   * gemeinsam hinaus, sobald der Fokus die Zeile verlässt; danach lädt die Liste
   * nach, weil Bedarf, Gewicht und die Summen darunter aus der Zeile gerechnet
   * werden und in der Antwort der einzelnen Zeile nicht stehen.
   */
  const neueZeile = useMemo(() => ({ ...NEUE_ZEILE, quantityBasis: vorgabeBezug }), [vorgabeBezug])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: zeilenId ? `bom:${zeilenId}` : `bom:neu:${positionId}`,
    bezeichnung: z?.name,
    daten: (z ?? neueZeile) as unknown as Datensatz,
    werte: alsDatensatz,
    schreibe: zeilenId
      ? async (patch, stand) => {
          const antwort = await api.patch<{ data: BomLine }>(`/projects/bom/${zeilenId}`, patch, mitStand(stand))
          void onGeaendert()
          return antwort.data.data as unknown as Datensatz
        }
      : () => Promise.reject(new Error('Die leere Zeile wird angelegt, nicht geändert.')),
    keepalive: zeilenId ? { url: `/projects/bom/${zeilenId}`, method: 'PATCH' } : undefined,
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: z?.name, gesperrt: !z })
  const daten = useStellenDaten(stelle)

  // Die Felder der Zeile. Alle gehören zur Gruppe, die hier außerhalb ihres
  // Kontexts entsteht — deshalb steht sie in den Optionen.
  const menge = useFeld<number | null>(stelle, 'quantity', { gruppe, art: 'zahl', pflicht: true, label: 'Menge', gesperrt: sperreBestellt })
  const einheit = useFeld<string | null>(stelle, 'unitId', { gruppe, art: 'auswahl', label: 'Einheit', gesperrt: sperreBestellt || umgerechnet })
  const name = useFeld(stelle, 'name', { gruppe, pflicht: true, label: 'Bezeichnung', gesperrt: sperre })
  const artikel = useFeld<string | null>(stelle, 'articleId', { gruppe, label: 'Artikel', gesperrt: sperre })
  const kategorie = useFeld<string | null>(stelle, 'categoryId', { gruppe, art: 'auswahl', label: 'Kategorie', gesperrt: sperre })
  const mengenbezug = useFeld<string | null>(stelle, 'quantityBasis', { gruppe, art: 'auswahl', label: 'Mengenbezug', gesperrt: sperreBestellt })
  const ep = useFeld<number | null>(stelle, 'purchasePrice', { gruppe, art: 'zahl', stellen: 4, label: 'EP', gesperrt: sperre || !preise })
  const pe = useFeld<number | null>(stelle, 'priceUnit', {
    gruppe,
    art: 'zahl',
    stellen: 0,
    pflicht: true,
    label: 'Preiseinheit',
    gesperrt: sperre || !preise,
    pruefe: (v) => (v !== null && v > 0 ? null : 'Die Preiseinheit muss größer als 0 sein'),
  })
  const rabatt = useFeld<number | null>(stelle, 'discountPercent', { gruppe, art: 'zahl', stellen: 2, label: 'Rabatt', gesperrt: sperre || !preise })
  // Umgerechnet ändert die Alternativmenge die Menge in kg — bestellt ist sie dann gesperrt wie die Menge.
  const altMenge = useFeld<number | null>(stelle, 'altQuantity', { gruppe, art: 'zahl', label: 'Alternativmenge', gesperrt: umgerechnet ? sperreBestellt : sperre })
  const altEinheit = useFeld<string | null>(stelle, 'altUnitId', { gruppe, art: 'auswahl', label: 'Alternativeinheit', gesperrt: sperre || umgerechnet })
  const altGewicht = useFeld<number | null>(stelle, 'altWeightPerUnit', {
    gruppe,
    art: 'zahl',
    stellen: 4,
    label: `kg je ${z?.altUnit?.code ?? 'Einheit'}`,
    gesperrt: sperreBestellt || !umgerechnet,
    pruefe: (v) => (!umgerechnet || (v !== null && v > 0) ? null : 'Das Gewicht je Einheit muss größer als 0 sein'),
  })
  const langtext = useFeld(stelle, 'longText', { gruppe, label: 'Langtext', gesperrt: sperre })
  const lieferantNr = useFeld(stelle, 'supplierArticleNumber', { gruppe, label: 'Lieferanten-ArtNr', gesperrt: sperre })
  const gewicht = useFeld<number | null>(stelle, 'weightPerUnit', { gruppe, art: 'zahl', stellen: 4, label: 'kg je Einheit', gesperrt: sperre || umgerechnet })
  const gruppierung = useFeld(stelle, 'grouping', { gruppe, label: 'Gruppierung', gesperrt: sperre })
  const werkstoff = useFeld(stelle, 'material', { gruppe, label: 'Werkstoff', gesperrt: sperre })
  const zeugnis = useFeld<string | null>(stelle, 'certificateType', {
    gruppe,
    art: 'auswahl',
    label: 'Werkszeugnis',
    gesperrt: sperre,
    // Die Zeugnispflicht ist keine zweite Eingabe: Sie folgt der Art im selben PATCH.
    folge: (v) => ({ requiresCertificate: v !== null }),
  })
  const verzinkbar = useFeld<boolean>(stelle, 'galvanizingSuitabilityRequired', { gruppe, art: 'schalter', label: 'feuerverzinkbar', gesperrt: sperre })
  const bezug = useFeld<string | null>(stelle, 'procurementSource', { gruppe, art: 'auswahl', label: 'Bezug der Ware', gesperrt: sperreBestellt })
  const einkauf = bezug.wert === 'PURCHASE'
  const lieferant = useFeld<string | null>(stelle, 'supplierId', {
    gruppe,
    label: 'Lieferant',
    gesperrt: sperreBestellt || !einkauf,
    schreib: lieferantText,
    // Ein geleertes Feld löst den Lieferanten; ein getippter, aber nicht
    // gewählter Name ändert nichts — er bleibt beim gespeicherten Wert.
    lies: (text) => {
      const t = text.trim()
      if (t === '') return null
      for (const [id, n] of lieferantenNamen.current) if (n === t) return id
      return (stelle.basis('supplierId') as string | null) ?? null
    },
  })
  const termin = useFeld(stelle, 'deliveryDueAt', { gruppe, art: 'datum', label: 'Liefertermin', gesperrt: sperre })

  // Der GP rechnet mit, sobald Menge, Mengenbezug, EP, Preiseinheit oder Rabatt getippt sind — nicht erst, wenn
  // die Zeile verlassen und gespeichert ist (Auftraggeber 7.10.2026); mit der Formel des Servers. Die leere Zeile
  // zeigt ihn, sobald ein Preis dasteht (leere Menge zählt 1, wie beim Anlegen). „je Einheit" braucht die Menge
  // der Position; kennt die Maske sie nicht, bleibt es beim Wert des Servers.
  const jeEinheit = mengenbezug.wert === 'PER_UNIT'
  const gp =
    (!z || eingabeOffen(menge, mengenbezug, ep, pe, rabatt)) && (!jeEinheit || positionsMenge !== undefined)
      ? bomEinkaufSoll(
          { quantity: menge.wert ?? (z ? 0 : 1), quantityBasis: jeEinheit ? 'PER_UNIT' : 'TOTAL', purchasePrice: ep.wert ?? null, priceUnit: pe.wert ?? 1, discountPercent: rabatt.wert ?? null },
          positionsMenge ?? 1,
        )
      : (z?.purchaseTotal ?? null)
  const terminNotiz = useFeld(stelle, 'deliveryNote', { gruppe, label: 'Anmerkung zum Liefertermin', gesperrt: sperre })
  const notiz = useFeld(stelle, 'internalNote', { gruppe, label: 'Anmerkung intern', gesperrt: sperre })

  const [vorschlaege, setVorschlaege] = useSuche<BomSuggestion>(name.props.value, sucheName, (q) => suggestBom(q, quelle))
  const [lieferantTreffer, setLieferantTreffer] = useSuche<{ id: string; name: string }>(lieferant.props.value, sucheLieferant, searchSuppliers)

  /** Nur zur Anzeige: Die Nummer kommt vom Server, ein eben gewählter Vorschlag zeigt sie sofort. */
  const gewaehlteNummer = useRef<string | null>(null)
  const artikelNummer =
    artikel.wert === null
      ? null
      : artikel.wert === ((daten.articleId as string | null) ?? null)
        ? ((daten.article as { articleNumber?: string } | null)?.articleNumber ?? null)
        : gewaehlteNummer.current
  const einheitCode = einheiten.find((u) => u.id === einheit.wert)?.code

  /** Feldschlüssel → Feld: Daraus liest die leere Zeile, was sie anlegen soll. */
  const felder: Record<string, { wert: unknown; gespeichert: unknown }> = {
    name,
    quantity: menge,
    unitId: einheit,
    articleId: artikel,
    categoryId: kategorie,
    quantityBasis: mengenbezug,
    purchasePrice: ep,
    priceUnit: pe,
    discountPercent: rabatt,
    altQuantity: altMenge,
    altUnitId: altEinheit,
    longText: langtext,
    supplierArticleNumber: lieferantNr,
    supplierId: lieferant,
    weightPerUnit: gewicht,
    grouping: gruppierung,
    material: werkstoff,
    certificateType: zeugnis,
    galvanizingSuitabilityRequired: verzinkbar,
    procurementSource: bezug,
    deliveryDueAt: termin,
    deliveryNote: terminNotiz,
    internalNote: notiz,
  }

  /** Fall 2 „Anlegen": Bezeichnung und Menge müssen dastehen, alles Übrige geht mit, wenn es von der Vorgabe abweicht. */
  async function anlegen(perTastatur: boolean, danach?: (neu: BomLine) => Promise<unknown>) {
    const bezeichnung = (name.wert ?? '').trim()
    const anzahl = menge.wert ?? 1
    if (!onAnlegen || !bezeichnung || !(anzahl > 0) || legtAn.current) return
    legtAn.current = true
    const dto: Record<string, unknown> = {}
    for (const [key, f] of Object.entries(felder)) if (!gleich(f.wert, f.gespeichert)) dto[key] = f.wert
    // Beim Ändern besorgt das Folgefeld die Zeugnispflicht — beim Anlegen gibt es keines.
    if (zeugnis.wert !== null) dto.requiresCertificate = true
    dto.name = bezeichnung
    dto.quantity = anzahl
    dto.quantityBasis = mengenbezug.wert ?? 'TOTAL'
    try {
      setFehler(null)
      await onAnlegen(dto as BomLineInput, perTastatur, danach)
    } catch (e) {
      legtAn.current = false
      setFehler(apiErrorText(e, 'Die Zeile ließ sich nicht anlegen.'))
    }
  }

  /**
   * AP-37 N3-8 — in kg umrechnen bzw. zurück (wie im Kalkulationsblatt). Was in
   * der Zeile noch nicht gespeichert ist, geht vorher hinaus; die leere Zeile
   * wird dafür angelegt.
   */
  async function umrechnen(to: 'WEIGHT' | 'ORIGINAL') {
    setFehler(null)
    setRechnet(true)
    try {
      if (!z) {
        await anlegen(false, (neu) => convertBomLine(neu.id, to, neu.updatedAt))
        return
      }
      const ergebnis = await gruppe.verlassen()
      if (ergebnis && ergebnis.art !== 'gespeichert') return
      const stand = (stelle.basis('updatedAt') as string | undefined) ?? z.updatedAt
      const neu = await convertBomLine(z.id, to, stand)
      stelle.uebernimm(neu as unknown as Datensatz)
      await onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Umrechnen hat nicht geklappt.'))
    } finally {
      setRechnet(false)
    }
  }

  // Was „in kg" ergäbe — aus den Werten, die gerade in der Zeile stehen (auch ungespeichert).
  const kgJeEinheit = gewicht.wert ?? 0
  // In der leeren Zeile zählt eine leere Menge 1, wie beim Anlegen — sonst böte der Knopf „0 kg als Menge" an.
  const inKg = Math.round((menge.wert ?? (z ? 0 : 1)) * kgJeEinheit * 1000) / 1000
  const einheitIstKg = (einheiten.find((u) => u.id === einheit.wert)?.code ?? '').trim().toLowerCase() === 'kg'
  const kannUmrechnen = !sperreBestellt && !umgerechnet && kgJeEinheit > 0 && Boolean(einheit.wert) && !einheitIstKg && Boolean(z || (name.wert ?? '').trim())

  /** Der Fokus hat die Zeile verlassen — die bestehende Zeile speichert die Gruppe selbst. */
  function verlassen(perTastatur: boolean) {
    setSucheName(false)
    setSucheLieferant(false)
    setSucheGruppe(null)
    setVorschlaege([])
    setLieferantTreffer([])
    // Ein nicht gewählter Lieferantenname wird nicht gespeichert: Er darf auch nicht stehen bleiben.
    if (gleich(lieferant.wert, lieferant.gespeichert) && lieferant.props.value !== lieferantText(lieferant.gespeichert)) {
      lieferant.tippe(lieferantText(lieferant.gespeichert))
    }
    if (!z) void anlegen(perTastatur)
  }

  function uebernimm(v: BomSuggestion) {
    setSucheName(false)
    setVorschlaege([])
    gewaehlteNummer.current = v.kind === 'ARTICLE' ? (v.articleNumber ?? null) : null
    name.tippe(v.name)
    if (v.kind === 'ARTICLE') {
      artikel.tippe(v.id)
      // Eine in kg umgerechnete Zeile hält Einheit und Gewicht fest (AP-37 N3-8) — beides ändert nur, wer zurückschaltet.
      if (umgerechnet) return
      // Das Gewicht je Einheit steht gleich nach der Wahl in der Zeile, nicht erst nach dem Speichern
      // (Auftraggeber 13.10.2026) — so ist auch „… kg als Menge" sofort da. Es gilt je Einheit des Artikels,
      // und die übernimmt die Zeile hier mit.
      if (v.weightPerUnit != null) gewicht.tippe(zahlText(v.weightPerUnit, 4))
      // Die Einheit ist eine Auswahl und schickt die Zeile: Sie kommt zuletzt,
      // damit alles Übernommene in einem PATCH geht.
      einheit.tippe(v.unit?.id ?? einheit.props.value)
      return
    }
    // Bisherige Eingabe: Freitextzeile mit den Angaben der früheren Zeile (8.1).
    artikel.tippe('')
    langtext.tippe(v.longText ?? '')
    werkstoff.tippe(v.material ?? '')
    gewicht.tippe(zahlText(v.weightPerUnit, 4))
    if (v.supplier) lieferantenNamen.current.set(v.supplier.id, v.supplier.name)
    lieferant.tippe(v.supplier?.name ?? '')
    lieferantNr.tippe(v.supplierArticleNumber ?? '')
    if (v.purchasePrice !== undefined) {
      ep.tippe(zahlText(v.purchasePrice, 4))
      pe.tippe(String(v.priceUnit ?? 1))
    }
    einheit.tippe(v.unit?.id ?? '')
  }

  const einheitAuswahl = (f: SpeicherFeld<string | null>, extra: Record<string, unknown> = {}) => (
    <select className={zelle} {...f.props} {...extra}>
      <option value="">—</option>
      {einheiten.map((u) => (
        <option key={u.id} value={u.id}>
          {u.code}
        </option>
      ))}
    </select>
  )

  /** Freigabe erteilen oder widerrufen — in beiden Anordnungen dasselbe Kästchen (N15-9). */
  async function freigabe(art: 'INQUIRY' | 'ORDER', an: boolean) {
    if (!z) return
    setFehler(null)
    try {
      if (an) await approveBomLines([z.id], art)
      else await revokeBomApproval([z.id], art)
      await onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Freigabe ließ sich nicht ändern.'))
    }
  }

  /**
   * Ein Knopf der Zeile öffnet ein Fenster (Zeugnisse, Dateien). Er nimmt den Fokus erst **mit** dem Klick, nicht
   * schon beim Drücken (`ohneFokus`): So kommt der Klick an, auch wenn eine Zeile darüber gerade offen steht und
   * beim Fokuswechsel zuklappte. Danach ist diese Zeile die bearbeitete, und das Fenster gibt den Fokus beim
   * Schließen an den Knopf zurück.
   */
  const oeffne = (f: Exclude<Fenster, null>) => (e: ReactMouseEvent<HTMLElement>) => {
    umschalten(f)
    e.currentTarget.focus()
  }

  // Was die Zeile über Lieferung und Bedarf sagt — zugeklappt in der Kurzinfo (breit) bzw. im Block (schmal),
  // aufgeklappt unter „Bedarf" bzw. im Hinweis.
  const lage = z
    ? [z.procurementSource === 'PURCHASE' ? z.supplier?.name : PROCUREMENT_SOURCE_LABELS[z.procurementSource], z.deliveryDueAt ? `Liefertermin ${alsTag(z.deliveryDueAt)}` : null]
        .filter(Boolean)
        .join(' · ')
    : ''
  const bedarfText = (mitGewicht: boolean) =>
    z
      ? [
          z.source === 'CAD' ? 'aus Import' : z.source === 'ARTICLE' ? 'aus dem Artikelstamm' : 'Freitext',
          `Bedarf ${zahl(z.requirement)} ${z.unit?.code ?? ''}`.trim(),
          `Bestellmenge ${zahl(z.orderSuggestion)}${z.packSize ? ` (Gebinde ${zahl(z.packSize)})` : ''}`,
          mitGewicht ? `${zahl(z.totalWeight, 1)} kg` : null,
          bestellt ? 'bestellt: Menge, Lieferant und Löschen gesperrt' : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : ''
  const mehrbedarf = z && z.requirementIncrease !== null && <span className="ml-1 text-amber-700 dark:text-amber-400">· +{zahl(z.requirementIncrease)} ggü. bestellt</span>

  // ── Felder, die in beiden Anordnungen stehen — nur an anderer Stelle ─────
  const lieferantFeld = (
    <div className="relative">
      <input
        className={zelle}
        {...lieferant.props}
        value={einkauf ? lieferant.props.value : ''}
        placeholder={einkauf ? 'suchen …' : PROCUREMENT_SOURCE_LABELS[(bezug.wert ?? 'PURCHASE') as ProcurementSource]}
        onChange={(e) => {
          lieferant.props.onChange(e)
          setSucheLieferant(true)
        }}
      />
      <Vorschlaege
        allein
        eintraege={lieferantTreffer}
        schluessel={(l) => l.id}
        onWahl={(l) => {
          lieferantenNamen.current.set(l.id, l.name)
          lieferant.setze(l.id)
          setSucheLieferant(false)
          setLieferantTreffer([])
        }}
        darstellung={(l) => l.name}
      />
    </div>
  )
  const gewichtLabel = umgerechnet ? `kg je ${z?.altUnit?.code ?? 'Einheit'}` : 'kg je Einheit'
  const gewichtFeld = (
    <>
      {umgerechnet ? (
        <span className="flex items-center gap-1.5">
          <input className={`${zelle} text-right`} inputMode="decimal" {...altGewicht.props} />
          <span className="whitespace-nowrap text-xs text-muted-foreground">kg / {z?.altUnit?.code ?? 'Einheit'}</span>
        </span>
      ) : (
        <span className="flex items-center gap-1.5">
          <input className={`${zelle} text-right`} inputMode="decimal" {...gewicht.props} />
          <span className="whitespace-nowrap text-xs text-muted-foreground">kg / {einheitCode ?? 'Einheit'}</span>
        </span>
      )}
      {/* Nachtrag 15 (N15-13): Alles zum Gewicht steht unter der Alternativmenge — auch das Gewicht der Zeile. */}
      {breit && z?.totalWeight ? <span className="mt-0.5 block px-1.5 text-[11px] tabular-nums text-muted-foreground">= {zahl(z.totalWeight, 1)} kg</span> : null}
      {/* AP-37 N3-8: der Umschaltpfeil — Gewicht als Menge, Kilopreis; und zurück. */}
      {umgerechnet ? (
        <button
          type="button"
          className="mt-1 inline-flex max-w-full items-center gap-1 rounded border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
          disabled={sperreBestellt || rechnet}
          title={`Zurück zur eingegebenen Menge — der Einkaufspreis gilt dann wieder je ${z?.altUnit?.code ?? 'Einheit'}`}
          onClick={() => void umrechnen('ORIGINAL')}
        >
          <ArrowLeftRight size={12} className="shrink-0" />
          {/* In der schmalen Spalte bricht der Text um, statt abgeschnitten zu werden (Browserprobe). */}
          <span className="text-left leading-tight">
            zurück in {zahl(z?.altQuantity ?? null)} {z?.altUnit?.code ?? ''}
          </span>
        </button>
      ) : (
        kannUmrechnen && (
          <button
            type="button"
            className="mt-1 inline-flex max-w-full items-center gap-1 rounded border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
            disabled={rechnet}
            title="Das Gewicht wird zur Menge, der Einkaufspreis zum Kilopreis — die eingegebene Menge bleibt als Alternativmenge stehen"
            onClick={() => void umrechnen('WEIGHT')}
          >
            <ArrowLeftRight size={12} className="shrink-0" />
            <span className="text-left leading-tight">{zahl(inKg)} kg als Menge</span>
          </button>
        )
      )}
    </>
  )
  // Nachtrag 15 (N15-15): Auswahlfeld — vorgeschlagen wird, was in dieser Position schon steht; eine neue
  // Gruppierung lässt sich weiter eintippen, sonst könnte keine entstehen. Beim Fokus stehen alle da, beim
  // Tippen die passenden; was schon im Feld steht, wird nicht noch einmal angeboten.
  const gruppeText = gruppierung.props.value.trim().toLowerCase()
  const gruppenTreffer = !sucheGruppe
    ? []
    : gruppierungen.filter((g) => g.toLowerCase() !== gruppeText && (sucheGruppe === 'alle' || g.toLowerCase().includes(gruppeText)))
  const gruppierungFeld = (
    <div className="relative">
      <input
        className={zelle}
        aria-label="Gruppierung"
        autoComplete="off"
        {...gruppierung.props}
        onFocus={() => setSucheGruppe('alle')}
        onChange={(e) => {
          gruppierung.props.onChange(e)
          setSucheGruppe('passende')
        }}
        onBlur={() => {
          gruppierung.props.onBlur()
          setSucheGruppe(null)
        }}
      />
      <Vorschlaege
        eintraege={gruppenTreffer}
        schluessel={(g) => g}
        onWahl={(g) => {
          gruppierung.tippe(g)
          setSucheGruppe(null)
        }}
        darstellung={(g) => g}
      />
    </div>
  )
  const zeugnisAuswahl = (
    <select className={zelle} aria-label="Werkszeugnis" {...zeugnis.props}>
      <option value="">nicht erforderlich</option>
      {MATERIAL_CERTIFICATE_TYPES.map((c) => (
        <option key={c} value={c}>
          {MATERIAL_CERTIFICATE_TYPE_LABELS[c]}
        </option>
      ))}
    </select>
  )
  const bezugAuswahl = (
    <select className={zelle} {...bezug.props}>
      {Object.entries(PROCUREMENT_SOURCE_LABELS).map(([k, l]) => (
        <option key={k} value={k}>
          {l}
        </option>
      ))}
    </select>
  )
  const artNrAnzeige = <span className="block truncate px-1.5 py-1 font-mono text-sm">{artikelNummer ?? <span className="text-muted-foreground">—</span>}</span>

  // ── Schmal: Zeilen 2 bis 4, spaltengenau unter Zeile 1 (Nachtrag 5a) ────
  const zeile2 = (
    <>
      <RasterFeld von="menge" label="Alternativmenge" schmal="w-24">
        <input className={`${zelle} text-right`} inputMode="decimal" {...altMenge.props} />
      </RasterFeld>
      <RasterFeld von="einheit" label={null} schmal="w-20">
        {einheitAuswahl(altEinheit, { 'aria-label': 'Alternativeinheit', 'data-weiter': KETTE.name })}
      </RasterFeld>
      <RasterFeld von="name" label="Langtext" schmal="w-full max-w-xl">
        <textarea rows={2} className={zelle} data-haupt={KETTE.langtext} {...langtext.props} />
      </RasterFeld>
      <RasterFeld von="kategorie" label="ArtNr" schmal="w-36">
        {artNrAnzeige}
      </RasterFeld>
      <RasterFeld von="mengenbezug" label="Lieferanten-ArtNr" schmal="w-40">
        <input className={zelle} {...lieferantNr.props} />
      </RasterFeld>
      <RasterFeld von="ep" bis="rabatt" label="Lieferant" schmal="w-56">
        {lieferantFeld}
      </RasterFeld>
    </>
  )
  const zeile3 = (
    <>
      <RasterFeld von="menge" bis="einheit" label={gewichtLabel} schmal="w-40">
        {gewichtFeld}
      </RasterFeld>
      <RasterFeld von="name" label="Gruppierung" schmal="w-40">
        {gruppierungFeld}
      </RasterFeld>
      <RasterFeld von="kategorie" label="Werkstoff" schmal="w-36">
        <input className={zelle} {...werkstoff.props} />
      </RasterFeld>
      <RasterFeld von="mengenbezug" bis="ep" label="Werkszeugnis" schmal="w-52">
        {zeugnisAuswahl}
      </RasterFeld>
      <RasterFeld von="pe" bis="gp" label={null} schmal="w-36">
        <span className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
          <input type="checkbox" {...verzinkbar.schalter} />
          feuerverzinkbar
        </span>
      </RasterFeld>
    </>
  )
  const zeile4 = (
    <>
      <RasterFeld von="menge" bis="einheit" label="Bezug der Ware" schmal="w-40">
        {bezugAuswahl}
      </RasterFeld>
      <RasterFeld von="name" bis="kategorie" label={<span className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-2"><span className="truncate">Liefertermin</span><span className="truncate">Anmerkung zum Liefertermin</span></span>} schmal="w-full max-w-xl">
        <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-2">
          <span>
            <input type="date" aria-label="Liefertermin" className={`${zelle} ${z?.overdue ? 'font-medium text-destructive' : ''}`} {...termin.props} />
            {z?.originalDeliveryDueAt && <span className="block text-[11px] text-muted-foreground line-through">{alsTag(z.originalDeliveryDueAt)}</span>}
          </span>
          <input className={zelle} aria-label="Anmerkung zum Liefertermin" {...terminNotiz.props} />
        </div>
      </RasterFeld>
      <RasterFeld von="mengenbezug" bis="gp" label="Anmerkung intern" schmal="w-72">
        <input className={zelle} {...notiz.props} />
      </RasterFeld>
    </>
  )

  // ── Breit: zwei Zeilen, beschriftete Felder nebeneinander (Nachtrag 15, N15-11) ──
  const beleg = z ? (z.orderDocument ?? z.inquiryDocument) : null
  const belegKnopf = z && beleg && (
    <button type="button" className="max-w-full truncate text-left hover:text-primary hover:underline" onClick={() => onOpenDocument?.(beleg.id)}>
      {z.orderDocument ? 'Bestellung' : 'Preisanfrage'} {beleg.number}
    </button>
  )
  const arten = freigabeArten(approvalMode)
  // Eine nicht-textliche Beschriftung macht aus dem Feld ein `<div>` statt eines `<label>`: Ein Klick auf den
  // Text darin träfe sonst das erste Bedienelement des Felds (den Beleg, die Zeugnis-Auswahl).
  const titel = (text: string) => <span>{text}</span>
  const zeileBreit2 = (
    <>
      <RasterFeld von="menge" label="Alternativmenge">
        <input className={`${zelle} text-right`} inputMode="decimal" {...altMenge.props} />
      </RasterFeld>
      <RasterFeld von="einheit" label={null}>
        {einheitAuswahl(altEinheit, { 'aria-label': 'Alternativeinheit', 'data-weiter': KETTE.name })}
      </RasterFeld>
      <RasterFeld von="name" label="Langtext">
        <textarea rows={2} className={zelle} data-haupt={KETTE.langtext} {...langtext.props} />
      </RasterFeld>
      {/* N15-12: Der Lieferant steht grundsätzlich in der zweiten Zeile, nie in der ersten. */}
      <RasterFeld von="werkstoff" bis="kategorie" label="Lieferant">
        {lieferantFeld}
      </RasterFeld>
      <RasterFeld von="mengenbezug" bis="ep" label="Liefertermin">
        <input type="date" className={`${zelle} ${z?.overdue ? 'font-medium text-destructive' : ''}`} {...termin.props} />
        {z?.originalDeliveryDueAt && <span className="block text-[11px] text-muted-foreground line-through">{alsTag(z.originalDeliveryDueAt)}</span>}
      </RasterFeld>
      <RasterFeld von="pe" bis="gp" label="Anmerkung zum Liefertermin">
        <input className={zelle} {...terminNotiz.props} />
      </RasterFeld>
      {z && (
        <RasterFeld von="status" bis="freigaben" label={titel('Belege und Freigaben')}>
          {/* N15-9: Wer freigegeben hat, steht hier — in der ersten Zeile nur das Datum. */}
          <span className="block px-1.5 py-1 text-xs leading-4 text-muted-foreground">
            {belegKnopf && <span className="block truncate">{belegKnopf}</span>}
            {z.procurementSource !== 'PURCHASE' ? (
              arten.length > 0 && <span className="block truncate">Freigaben nicht nötig ({PROCUREMENT_SOURCE_LABELS[z.procurementSource]})</span>
            ) : (
              arten.map((art) => {
                const f = art === 'ORDER' ? z.approvals.order : z.approvals.inquiry
                const text = `${FREIGABE_NAME[art]}: ${f ? `${f.byName ?? '—'} · ${alsTag(f.at)}` : 'nicht freigegeben'}`
                return (
                  <span key={art} className="block truncate" title={text}>
                    {text}
                  </span>
                )
              })
            )}
          </span>
        </RasterFeld>
      )}
      <RasterFeld von="zeugnis" bis="aktionen" label={titel('Werkszeugnis')}>
        {zeugnisAuswahl}
        <label className="mt-1 flex items-center gap-1.5 px-0.5 text-xs text-muted-foreground">
          <input type="checkbox" {...verzinkbar.schalter} />
          feuerverzinkbar
        </label>
      </RasterFeld>
    </>
  )
  const zeileBreit3 = (
    <>
      {/* N15-13: kg je Einheit, Zeilengewicht und Umrechnen — alles unter der Alternativmenge. */}
      <RasterFeld von="menge" bis="einheit" label={gewichtLabel}>
        {gewichtFeld}
      </RasterFeld>
      <RasterFeld
        von="name"
        label={
          <span className="grid grid-cols-[minmax(0,13.75rem)_minmax(0,1fr)] gap-x-2">
            <span className="truncate">Gruppierung</span>
            <span className="truncate">ArtNr</span>
          </span>
        }
      >
        <div className="grid grid-cols-[minmax(0,13.75rem)_minmax(0,1fr)] gap-x-2">
          {gruppierungFeld}
          {artNrAnzeige}
        </div>
      </RasterFeld>
      <RasterFeld von="werkstoff" bis="kategorie" label="Lieferanten-ArtNr">
        <input className={zelle} {...lieferantNr.props} />
      </RasterFeld>
      <RasterFeld von="mengenbezug" bis="ep" label="Bezug der Ware">
        {bezugAuswahl}
      </RasterFeld>
      <RasterFeld von="pe" bis="gp" label="Anmerkung intern">
        <input className={zelle} {...notiz.props} />
      </RasterFeld>
      {z && (
        <RasterFeld von="status" bis="freigaben" label={titel('Bedarf')}>
          {/* Ohne das Gewicht — es steht links unter der Alternativmenge. */}
          <span className="block px-1.5 py-1 text-xs leading-4 text-muted-foreground">
            {bedarfText(false)}
            {mehrbedarf}
          </span>
        </RasterFeld>
      )}
      {z && (
        <RasterFeld von="zeugnis" bis="aktionen" label={titel('Dateien')}>
          <button type="button" className="inline-flex max-w-full items-center gap-1 rounded border border-border px-2 py-0.5 text-xs hover:bg-muted" aria-expanded={fenster === 'dateien'} onClick={() => umschalten('dateien')}>
            <Paperclip size={12} className="shrink-0" /> <span className="truncate">{z.fileCount > 0 ? `${z.fileCount} ${z.fileCount === 1 ? 'Datei' : 'Dateien'}` : 'hinzufügen'}</span>
          </button>
        </RasterFeld>
      )}
    </>
  )

  // Die Alternativmenge rechtsbündig unter der Menge, ihre Einheit unter der Einheit (N3-10) — in beiden Anordnungen.
  const zuAlternativ = z && (
    <>
      <RasterInfo von="menge" rechts>
        {/* 1 px = Rahmen des Mengenfelds darüber: die Ziffern stehen bündig (nachgemessen). */}
        <span className="pr-px tabular-nums">{z.altQuantity !== null ? `${umgerechnet ? '= ' : ''}${zahl(z.altQuantity)}` : ''}</span>
      </RasterInfo>
      <RasterInfo von="einheit">{z.altQuantity !== null ? (z.altUnit?.code ?? '') : ''}</RasterInfo>
    </>
  )
  const zuInfo = !z ? undefined : breit ? (
    // Nachtrag 15: eine Zeile — was bisher der Hinweis unter der Zeile sagte, und unter dem Status der Beleg.
    <>
      {zuAlternativ}
      <RasterInfo von="name" bis="gp">
        {[lage, bedarfText(true)].filter(Boolean).join(' · ')}
        {mehrbedarf}
      </RasterInfo>
      <RasterInfo von="status" bis="aktionen">
        {/* AP-35 N17-1, N17-10: die Marke des Einkaufs vorn — abgeschnitten wird nur, was dahinter steht. */}
        {(z.offer || z.confirmation) && (
          <>
            <EinkaufsMarke z={z} />
            {(beleg || z.inDrafts.length > 0) && ' · '}
          </>
        )}
        {belegKnopf}
        {z.inDrafts.length > 0 && `${beleg ? ' · ' : ''}im Entwurf ${z.inDrafts.join(', ')}`}
      </RasterInfo>
    </>
  ) : (
    (z.altQuantity !== null || z.material) && (
      <>
        {zuAlternativ}
        <RasterInfo von="name">{z.material ?? ''}</RasterInfo>
      </>
    )
  )

  // Die Fenster der Zeile. Breit hängen sie an der Spalte ganz rechts und gehen über die Felder nach links auf,
  // schmal am Block unter den Feldern.
  const fensterDerZeile = z && (
    <>
      {fenster === 'zeugnis' && (
        <Aufklapper titel={`Zeugnisse · ${z.name}`} nachLinks={breit} onSchliessen={() => setFenster(null)}>
          <ZeugnisFenster z={z} darfZeugnis={darfZeugnis} onGeaendert={onGeaendert} onOpenDocument={onOpenDocument} />
        </Aufklapper>
      )}
      {fenster === 'dateien' && (
        <Aufklapper titel={`Dateien · ${z.name}`} breit nachLinks={breit} onSchliessen={() => setFenster(null)}>
          <ProjectFilesPanel projectId={projectId} entityType="BOM_LINE" entityId={z.id} onChanged={onGeaendert} />
        </Aufklapper>
      )}
    </>
  )
  const darfLoeschen = darfPflegen && !bestellt && Boolean(onLoeschen)

  return (
    <ZeilenBlock
      neu={!z}
      autoFokus={autoFokus}
      gedimmt={storniert}
      schluessel={z?.id}
      gezogen={gezogen}
      ziellinie={ziellinie}
      ziehbar={Boolean(z && ziehbar)}
      markiert={markiert}
      onRand={z ? onRand : undefined}
      randLabel={z?.name}
      onDateien={z ? onDateien : undefined}
      tabKette
      gruppe={z ? gruppe : undefined}
      onVerlassen={verlassen}
      onVerwerfen={() => {
        gruppe.verwerfen()
        setFehler(null)
      }}
      fehler={fehler}
      zeilen={breit ? [zeileBreit2, zeileBreit3] : [zeile2, zeile3, zeile4]}
      zuInfo={zuInfo}
      // Schmal (N15-14): der Block als eine Zeile unter den Feldern — Freigaben, Zeugnis und Dateien immer zu
      // sehen. Die leere Zeile hat nichts davon und bekommt keinen Block.
      seite={
        !breit && z
          ? () => (
              <BlockDarunter
                z={z}
                lage={lage}
                approvalMode={approvalMode}
                darfFreigeben={darfFreigeben}
                darfLoeschen={darfLoeschen}
                fenster={fenster}
                onFenster={oeffne}
                onFreigabe={(art, an) => void freigabe(art, an)}
                onLoeschen={onLoeschen}
                onOpenDocument={onOpenDocument}
              >
                {fensterDerZeile}
              </BlockDarunter>
            )
          : undefined
      }
      // Schmal steht der Hinweis (Herkunft, Bedarf …) nur noch an der offenen Zeile — Lieferant und Liefertermin
      // zeigt der Block. Breit sagen es die Kurzinfo und das Feld „Bedarf".
      hinweis={
        breit
          ? undefined
          : (aktiv) =>
              z &&
              zeileOffen(klappModus, aktiv) && (
                <>
                  {bedarfText(true)}
                  {mehrbedarf}
                </>
              )
      }
    >
      <Feld spalte="menge">
        <input data-start data-haupt={KETTE.menge} className={`${zelle} text-right`} inputMode="decimal" placeholder={z ? '' : '1'} {...menge.props} />
      </Feld>
      <Feld spalte="einheit">{einheitAuswahl(einheit, { 'data-haupt': KETTE.einheit })}</Feld>
      <Feld spalte="name">
        <div className="relative">
          <input
            data-haupt={KETTE.name}
            className={`${zelle} font-medium`}
            placeholder={z ? '' : quelle === 'ARTICLES' ? 'neue Zeile — Artikel suchen oder Bezeichnung eingeben' : 'neue Zeile — aus bisherigen Eingaben'}
            {...name.props}
            onChange={(e) => {
              name.props.onChange(e)
              // In der neuen Zeile hebt weiteres Tippen die Artikelwahl wieder auf.
              if (!z && artikel.wert !== null) {
                gewaehlteNummer.current = null
                artikel.tippe('')
              }
              setSucheName(true)
            }}
          />
          <Vorschlaege
            allein
            eintraege={vorschlaege}
            schluessel={(v) => `${v.kind}-${v.id}`}
            onWahl={uebernimm}
            darstellung={(v) => (
              <>
                <span className="font-medium">{v.name}</span>
                <span className="ml-2 text-xs text-muted-foreground">{v.kind === 'ARTICLE' ? v.articleNumber : [v.material, v.supplier?.name].filter(Boolean).join(' · ')}</span>
              </>
            )}
          />
        </div>
      </Feld>
      {breit && (
        // Nachtrag 15 (N15-8): der Werkstoff als Feld der ersten Zeile — dasselbe Feld wie schmal in Zeile 3,
        // ohne `data-haupt`: Die Tab-Kette geht an ihm vorbei (N15-19).
        <Feld spalte="werkstoff">
          <input className={zelle} aria-label="Werkstoff" {...werkstoff.props} />
        </Feld>
      )}
      <Feld spalte="kategorie">
        <select className={zelle} data-haupt={KETTE.kategorie} {...kategorie.props}>
          <option value="">—</option>
          {kategorien
            .filter((k) => k.isActive || k.id === z?.category?.id)
            .map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
        </select>
      </Feld>
      <Feld spalte="mengenbezug">
        <select className={zelle} title={`gesamt: Menge für die ganze ${terms.position} · je Einheit: Menge je Einheit der ${terms.position}`} {...mengenbezug.props}>
          <option value="TOTAL">gesamt</option>
          <option value="PER_UNIT">je Einheit</option>
        </select>
      </Feld>
      <Feld spalte="ep" leer={!preise}>
        {preise ? <input className={`${zelle} text-right`} inputMode="decimal" data-haupt={KETTE.ep} {...ep.props} /> : <span />}
      </Feld>
      <Feld spalte="pe" leer={!preise}>
        {preise ? <input className={`${zelle} text-right`} inputMode="numeric" data-haupt={KETTE.pe} title="Preiseinheit" {...pe.props} /> : <span />}
      </Feld>
      <Feld spalte="rabatt" leer={!preise}>
        {preise ? <input className={`${zelle} px-1 text-right`} inputMode="decimal" data-haupt={KETTE.rabatt} title="Rabatt in %" {...rabatt.props} /> : <span />}
      </Feld>
      <Feld spalte="gp" leer={!preise || (!z && gp === null)}>
        {preise && (z || gp !== null) ? <span className="block px-1.5 py-1 text-right text-sm tabular-nums">{geldText(gp)}</span> : <span />}
      </Feld>
      {breit && (
        // Nachtrag 15 (N15-8 bis N15-10): Status, Freigaben, Zeugnis, Dateien und die Aktionen als Spalten — immer
        // zu sehen, auch zugeklappt. Knöpfe und Kästchen nehmen beim Drücken keinen Fokus (`ohneFokus`) und stehen
        // nicht in der Tab-Reihenfolge (N15-19). Die leere Zeile lässt die Spalten frei.
        <>
          <Feld spalte="status">{z ? <StatusPille z={z} /> : <span />}</Feld>
          <Feld spalte="freigaben">
            {z ? <Freigaben z={z} approvalMode={approvalMode} darfFreigeben={darfFreigeben} onFreigabe={(art, an) => void freigabe(art, an)} /> : <span />}
          </Feld>
          <Feld spalte="zeugnis">{z ? <ZeugnisMarke z={z} kurz onClick={oeffne('zeugnis')} /> : <span />}</Feld>
          <Feld spalte="dateien">
            {z && z.fileCount > 0 ? (
              <button
                type="button"
                tabIndex={-1}
                className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground hover:text-foreground"
                title={`${z.fileCount} ${z.fileCount === 1 ? 'Datei' : 'Dateien'}`}
                aria-label={`${z.fileCount} ${z.fileCount === 1 ? 'Datei' : 'Dateien'}`}
                aria-expanded={fenster === 'dateien'}
                onMouseDown={ohneFokus}
                onClick={oeffne('dateien')}
              >
                <Paperclip size={13} /> {z.fileCount}
              </button>
            ) : (
              <span />
            )}
          </Feld>
          <Feld spalte="aktionen">
            {z ? (
              // `relative`: An dieser Spalte hängen die Fenster — rechtsbündig, sie gehen nach links über die Felder auf.
              <div className="relative flex justify-end text-muted-foreground">
                <ZeilenAktionen fenster={fenster} onFenster={oeffne} darfLoeschen={darfLoeschen} onLoeschen={onLoeschen} ohneTab />
                {fensterDerZeile}
              </div>
            ) : (
              <span />
            )}
          </Feld>
        </>
      )}
    </ZeilenBlock>
  )
}

// ── Status, Freigaben, Zeugnis, Dateien ─────────────────────────────────────
//
// Bis Nachtrag 15 ein Block rechts neben den Feldern. Seither stehen dieselben Bausteine breit als Spalten der
// ersten Zeile und schmal als eine Zeile unter den Feldern (`BlockDarunter`).

type Fenster = 'zeugnis' | 'dateien' | null
type FreigabeArt = 'INQUIRY' | 'ORDER'

const FREIGABE_NAME: Record<FreigabeArt, string> = { INQUIRY: 'Anfrage', ORDER: 'Bestellung' }

/** Welche Freigaben es gibt, bestimmt die Einstellung — die Anfrage steht vor der Bestellung. */
const freigabeArten = (modus: ProcurementApprovalMode): readonly FreigabeArt[] => (modus === 'INQUIRY_AND_ORDER' ? ['INQUIRY', 'ORDER'] : modus === 'ORDER_ONLY' ? ['ORDER'] : [])

/**
 * Der Druck auf einen Knopf oder ein Kästchen nimmt keinen Fokus — erst der Klick wirkt (wie `klickOhneFokus`
 * im Zeilenblock). Seit Nachtrag 15 stehen Freigaben, Zeugnis und Dateien an **jeder** Zeile, auch zugeklappt:
 * Nähme der Druck den Fokus, klappte die Zeile auf und eine eben bearbeitete darüber zu — das Element läge beim
 * Loslassen nicht mehr unter dem Zeiger, und der Klick käme nie an.
 */
const ohneFokus = (e: ReactMouseEvent) => e.preventDefault()

function StatusPille({ z }: { z: BomLine }) {
  // Nachtrag 16 (N16-7): Steht die Zeile erst in einem Entwurf, sagt die Marke, wodurch sie weiterrückt — sonst
  // liest sich „offen" neben einer angelegten Bestellung wie ein Fehler (Auftraggeber 13.10.2026).
  const imEntwurf = z.inDrafts.length > 0 && (z.procurementStatus === 'OPEN' || z.procurementStatus === 'INQUIRED')
  return (
    <span
      className={`inline-flex max-w-full truncate rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_FARBE[z.procurementStatus] ?? ''}`}
      title={
        imEntwurf
          ? `Der Beleg (${z.inDrafts.join(', ')}) ist noch ein Entwurf. „angefragt" bzw. „bestellt" wird die Zeile, sobald er festgeschrieben oder aus Symphenia per E-Mail versendet ist.`
          : undefined
      }
    >
      {PROCUREMENT_STATUS_LABELS[z.procurementStatus]}
    </span>
  )
}

/**
 * Die Freigaben einer Zeile als Kästchen — immer zu sehen (Nachtrag 15, N15-9). Als Spalte (breit) steht die
 * Bestellung unter der Anfrage und das Datum rechts daneben, der Name im Tooltip; `lang` (schmal, unter den
 * Feldern) stehen sie nebeneinander mit Name und Datum. Ohne Einkauf braucht die Zeile keine Freigabe.
 */
function Freigaben({
  z,
  approvalMode,
  darfFreigeben,
  onFreigabe,
  lang,
}: {
  z: BomLine
  approvalMode: ProcurementApprovalMode
  darfFreigeben: boolean
  onFreigabe: (art: FreigabeArt, an: boolean) => void
  lang?: boolean
}) {
  const arten = freigabeArten(approvalMode)
  if (!arten.length) return null
  if (z.procurementSource !== 'PURCHASE') {
    return (
      <span className="text-[11px] text-muted-foreground" title={`Bezug der Ware: ${PROCUREMENT_SOURCE_LABELS[z.procurementSource]}`}>
        {lang ? `Freigaben nicht nötig (${PROCUREMENT_SOURCE_LABELS[z.procurementSource]})` : 'nicht nötig'}
      </span>
    )
  }
  const eine = (art: FreigabeArt) => {
    const f = art === 'ORDER' ? z.approvals.order : z.approvals.inquiry
    // Mit Beleg lässt sich die Freigabe nicht mehr widerrufen (409 BOM_APPROVAL_LOCKED).
    const belegDa = Boolean(art === 'ORDER' ? z.orderDocument : z.inquiryDocument)
    const tipp = f ? `${FREIGABE_NAME[art]} freigegeben: ${f.byName ?? '—'} · ${alsTag(f.at)}${belegDa ? ' — Beleg vorhanden, nicht mehr widerrufbar' : ''}` : `${FREIGABE_NAME[art]} nicht freigegeben`
    const kaestchen = (
      <label className="inline-flex items-center gap-[5px] whitespace-nowrap" data-nicht-greifen onMouseDown={ohneFokus}>
        <input
          type="checkbox"
          tabIndex={-1}
          className="m-0 h-3 w-3"
          checked={Boolean(f)}
          disabled={!darfFreigeben || (Boolean(f) && belegDa)}
          onChange={(e) => onFreigabe(art, e.target.checked)}
        />
        {FREIGABE_NAME[art]}
      </label>
    )
    return lang ? (
      <span key={art} className="inline-flex items-center gap-1.5" title={tipp}>
        {kaestchen}
        {f && (
          <span className="text-muted-foreground">
            {f.byName ?? '—'} · {alsTag(f.at)}
          </span>
        )}
      </span>
    ) : (
      <span key={art} className="grid grid-cols-[5rem_minmax(0,1fr)] items-center" title={tipp}>
        {kaestchen}
        <span className="truncate text-[11px] tabular-nums text-muted-foreground">{f ? alsTag(f.at) : ''}</span>
      </span>
    )
  }
  // Als Spalte: 12-px-Schrift, zwei Zeilen zu 15 px — zusammen so hoch wie ein Eingabefeld.
  return lang ? <>{arten.map(eine)}</> : <span className="grid text-left text-xs leading-[15px]">{arten.map(eine)}</span>
}

/** Der Pfeil „Zeugnisse und Belege" und der Löschknopf — breit in der letzten Spalte, schmal am Ende des Blocks. */
function ZeilenAktionen({
  fenster,
  onFenster,
  darfLoeschen,
  onLoeschen,
  ohneTab,
}: {
  fenster: Fenster
  onFenster: (f: Exclude<Fenster, null>) => (e: ReactMouseEvent<HTMLElement>) => void
  darfLoeschen: boolean
  onLoeschen?: () => void
  /** Nicht in der Tab-Reihenfolge — als Spalte der ersten Zeile (N15-19). */
  ohneTab?: boolean
}) {
  return (
    <span className="flex shrink-0 text-muted-foreground">
      <button
        type="button"
        tabIndex={ohneTab ? -1 : undefined}
        className="p-0.5 hover:text-foreground"
        aria-label="Zeugnisse und Belege"
        title="Zeugnisse und Belege"
        aria-expanded={fenster === 'zeugnis'}
        onMouseDown={ohneFokus}
        onClick={onFenster('zeugnis')}
      >
        <ChevronRight size={14} />
      </button>
      {darfLoeschen && (
        <button type="button" tabIndex={ohneTab ? -1 : undefined} className="p-0.5 hover:text-destructive" aria-label="löschen" title="Zeile löschen" onMouseDown={ohneFokus} onClick={onLoeschen}>
          <Trash2 size={14} />
        </button>
      )}
    </span>
  )
}

/**
 * Schmale Anordnung (Nachtrag 15, N15-14): Status, Freigaben, Zeugnis, Dateien, Lieferant und Liefertermin als
 * **eine Zeile unter den Feldern** — an jeder Zeile, nicht erst an der bearbeiteten. `children` sind die Fenster
 * der Zeile; sie öffnen sich unter dem Block.
 */
function BlockDarunter({
  z,
  lage,
  approvalMode,
  darfFreigeben,
  darfLoeschen,
  fenster,
  onFenster,
  onFreigabe,
  onLoeschen,
  onOpenDocument,
  children,
}: {
  z: BomLine
  /** „Lieferant · Liefertermin …" — ohne Einkauf der Bezug der Ware. */
  lage: string
  approvalMode: ProcurementApprovalMode
  darfFreigeben: boolean
  darfLoeschen: boolean
  fenster: Fenster
  onFenster: (f: Exclude<Fenster, null>) => (e: ReactMouseEvent<HTMLElement>) => void
  onFreigabe: (art: FreigabeArt, an: boolean) => void
  onLoeschen?: () => void
  onOpenDocument?: (id: string) => void
  children?: ReactNode
}) {
  const beleg = z.orderDocument ?? z.inquiryDocument
  return (
    // Eine eigene Zeile über die ganze Breite: mittig ausgerichtet, die Aktionen am rechten Rand.
    <div className="flex w-full flex-wrap items-center gap-x-5 gap-y-1">
      <span className="inline-flex flex-wrap items-center gap-x-1.5 text-muted-foreground">
        <StatusPille z={z} />
        <EinkaufsMarke z={z} />
        {beleg && (
          <button type="button" className="hover:text-primary hover:underline" onMouseDown={ohneFokus} onClick={() => onOpenDocument?.(beleg.id)}>
            {z.orderDocument ? 'Bestellung' : 'Preisanfrage'} {beleg.number}
          </button>
        )}
        {z.inDrafts.length > 0 && <span>im Entwurf {z.inDrafts.join(', ')}</span>}
      </span>
      <Freigaben z={z} approvalMode={approvalMode} darfFreigeben={darfFreigeben} onFreigabe={onFreigabe} lang />
      <ZeugnisMarke z={z} onClick={onFenster('zeugnis')} />
      <button
        type="button"
        className="inline-flex items-center gap-1 whitespace-nowrap text-muted-foreground hover:text-foreground"
        title="Dateien der Zeile"
        aria-expanded={fenster === 'dateien'}
        onMouseDown={ohneFokus}
        onClick={onFenster('dateien')}
      >
        <Paperclip size={13} /> {z.fileCount > 0 ? `${z.fileCount} ${z.fileCount === 1 ? 'Datei' : 'Dateien'}` : 'keine Dateien'}
      </button>
      {lage && <span>{lage}</span>}
      <span className="ml-auto">
        <ZeilenAktionen fenster={fenster} onFenster={onFenster} darfLoeschen={darfLoeschen} onLoeschen={onLoeschen} />
      </span>
      {children}
    </div>
  )
}

/**
 * Die Zeugnis-Marke öffnet die Zeugnisse der Zeile. `kurz` (Nachtrag 15, N15-10) für die Spalte der breiten
 * Anordnung: „fehlt" rot, sonst nur die Art — grün, ein zu schwaches Zeugnis rot; der ganze Text steht im Tooltip.
 */
function ZeugnisMarke({ z, kurz, onClick }: { z: BomLine; kurz?: boolean; onClick: (e: ReactMouseEvent<HTMLElement>) => void }) {
  if (z.certificateState === 'none') return null
  const art = MATERIAL_CERTIFICATE_TYPE_LABELS[(z.certificateType ?? 'EN10204_3_1') as MaterialCertificateType].replace('EN 10204 ', '')
  const ok = z.certificateState === 'ok'
  const text = ok ? `Zeugnis ${art}` : z.certificateState === 'insufficient' ? `Zeugnis ${art} zu schwach` : 'Zeugnis fehlt'
  const tun = ok ? 'Zeugnisse ansehen und zuordnen' : 'Zeugnis zuordnen oder anlegen'
  return (
    <button
      type="button"
      tabIndex={kurz ? -1 : undefined}
      onMouseDown={ohneFokus}
      onClick={onClick}
      title={kurz ? `${text} — ${tun}` : tun}
      aria-label={kurz ? text : undefined}
      className={`inline-block max-w-full truncate rounded px-1.5 py-0.5 align-middle text-xs font-medium hover:underline ${
        ok ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-destructive/10 text-destructive'
      }`}
    >
      {kurz ? (z.certificateState === 'missing' ? 'fehlt' : art) : text}
    </button>
  )
}

/**
 * Fenster der Zeile (Zeugnisse, Dateien). Steht ein Block neben den Feldern, öffnet es nach links über die
 * Felder; steht er darunter, nach unten. `nachLinks` (Nachtrag 15) für die Spalte ganz rechts in der breiten
 * Stückliste: unter ihr, rechtsbündig — so geht es über die Felder nach links auf und nicht aus dem Bild. Esc
 * und ein Klick daneben schließen es — Esc läuft **nicht** weiter zum Zeilenblock, der sonst die Änderungen
 * der Zeile verwürfe.
 */
export function Aufklapper({
  titel,
  breit,
  nachLinks,
  onSchliessen,
  children,
}: {
  titel: string
  /** Für die Dateitabelle — sie hat sechs Spalten. */
  breit?: boolean
  /** Am rechten Rand der Liste verankert: unter dem Öffner, nach links ausgedehnt. */
  nachLinks?: boolean
  onSchliessen: () => void
  children: ReactNode
}) {
  const daneben = useSeiteDaneben()
  const ref = useRef<HTMLDivElement>(null)
  const schliessen = useRef(onSchliessen)
  schliessen.current = onSchliessen
  // Der Knopf, der das Fenster geöffnet hat: Esc und × geben ihm den Fokus
  // zurück — sonst verschwände er mit dem Fenster, und die Zeile bliebe ohne.
  const oeffner = useRef(document.activeElement as HTMLElement | null)
  const zu = () => {
    onSchliessen()
    if (oeffner.current?.isConnected) oeffner.current.focus()
  }

  useEffect(() => {
    const weg = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) schliessen.current()
    }
    // Esc schließt auch, wenn der Fokus gerade nicht im Fenster steht.
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !ref.current?.contains(e.target as Node)) schliessen.current()
    }
    document.addEventListener('mousedown', weg)
    document.addEventListener('keydown', taste)
    return () => {
      document.removeEventListener('mousedown', weg)
      document.removeEventListener('keydown', taste)
    }
  }, [])

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={titel}
      className={`absolute z-50 ${breit ? 'w-[44rem] overflow-x-auto' : 'w-[26rem]'} max-w-[calc(100vw-2rem)] rounded-lg border border-border p-3 text-xs shadow-xl ${nachLinks ? 'right-0 top-full mt-1 text-left' : daneben ? 'right-full top-0 mr-1.5' : 'left-0 top-full mt-1 max-w-full'}`}
      style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          e.preventDefault()
          zu()
        }
        // Tab bleibt im Fenster; die Kette der Zeile gilt hier nicht.
        if (e.key === 'Tab') e.stopPropagation()
      }}
      // Ein Klick auf Text im Fenster nähme sonst den Fokus aus der Zeile und
      // klappte sie zu, während man noch im Fenster arbeitet.
      onMouseDown={(e) => {
        if (!(e.target as HTMLElement).closest('input, select, textarea, button, a, label')) e.preventDefault()
      }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">{titel}</span>
        <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="schließen" onClick={zu}>
          <X size={16} />
        </button>
      </div>
      {children}
    </div>
  )
}

const heute = () => new Date().toISOString().slice(0, 10)

/**
 * Zeugnisse der Zeile: ansehen, lösen, vorhandenes zuordnen, neues anlegen
 * und zuordnen — und die Belege (AP-35 Nachtrag 5a, ersetzt das Detail unter
 * der Zeile). Die Suche ist mit dem Werkstoff der Zeile vorbelegt.
 */
function ZeugnisFenster({
  z,
  darfZeugnis,
  onGeaendert,
  onOpenDocument,
}: {
  z: BomLine
  darfZeugnis: boolean
  onGeaendert: () => Promise<void>
  onOpenDocument?: (id: string) => void
}) {
  const [suche, setSuche] = useState(z.material ?? '')
  const [kandidaten, setKandidaten] = useState<CertificateCandidate[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  /** Zählt Zuordnen und Lösen — die Trefferliste lädt danach neu. */
  const [stand, setStand] = useState(0)
  const [neu, setNeu] = useState({
    datei: null as File | null,
    number: '',
    type: (z.certificateType ?? 'EN10204_3_1') as string,
    heatNumber: '',
    material: z.material ?? '',
    issuer: z.supplier?.name ?? '',
    issuedAt: heute(),
  })

  useEffect(() => {
    if (!darfZeugnis) return
    const t = setTimeout(() => certificateCandidates(z.id, suche.trim() || undefined).then(setKandidaten).catch(() => setKandidaten([])), 250)
    return () => clearTimeout(t)
  }, [suche, z.id, darfZeugnis, stand])

  async function tu(aktion: () => Promise<string[] | void>) {
    setFehler(null)
    setHinweis(null)
    setLaeuft(true)
    try {
      const h = await aktion()
      if (h?.includes('projects.hints.certificateTypeInsufficient')) setHinweis('Hinweis: Das Zeugnis ist schwächer als gefordert.')
      await onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
      setStand((n) => n + 1)
    }
  }

  async function anlegen() {
    if (!neu.datei || !neu.number.trim() || !neu.issuer.trim()) return
    await tu(async () => {
      // Der Lieferant der Zeile wird als Kunde verknüpft, ein anderer Name bleibt Text.
      const alsLieferant = z.supplier && neu.issuer.trim() === z.supplier.name
      const zeugnis = await createCertificate(neu.datei!, {
        type: neu.type,
        number: neu.number.trim(),
        issuedAt: neu.issuedAt,
        heatNumber: neu.heatNumber.trim() || undefined,
        material: neu.material.trim() || undefined,
        issuerClientId: alsLieferant ? z.supplier!.id : undefined,
        issuerName: alsLieferant ? undefined : neu.issuer.trim() || undefined,
      })
      setNeu((n) => ({ ...n, datei: null, number: '', heatNumber: '' }))
      return assignCertificate(zeugnis.id, [z.id])
    })
  }

  const gefordert = z.requiresCertificate && z.certificateType ? MATERIAL_CERTIFICATE_TYPE_LABELS[z.certificateType] : 'keines'
  const teil = 'mt-2 border-t border-border pt-2'
  const titel = 'mb-1 text-[11px] font-semibold text-muted-foreground'
  const feld = 'w-full rounded border border-border/70 bg-background px-1.5 py-1 text-xs focus:border-ring focus:outline-none'

  return (
    <div>
      <p className="text-muted-foreground">
        gefordert: <b className="text-foreground">{gefordert}</b>
        {z.material && ` · Werkstoff ${z.material}`}
      </p>

      <div className={teil}>
        <div className={titel}>Zugeordnet</div>
        {z.certificates.length === 0 && <p className="text-muted-foreground">noch keines</p>}
        {z.certificates.map((c) => (
          <div key={c.id} className="flex items-baseline justify-between gap-2 py-0.5">
            <span className={c.isActive ? '' : 'line-through'}>
              <b>{c.number}</b> · {MATERIAL_CERTIFICATE_TYPE_LABELS[c.type].replace('EN 10204 ', '')}
              {c.heatNumber ? ` · Schmelze ${c.heatNumber}` : ''}
              {' · '}
              <button type="button" className="text-primary hover:underline" onClick={() => void oeffneZeugnis(c.id, c.number)}>
                PDF
              </button>
            </span>
            {darfZeugnis && (
              <button type="button" className="text-destructive hover:underline" onClick={() => void tu(() => unassignCertificate(z.id, c.id))}>
                lösen
              </button>
            )}
          </div>
        ))}
      </div>

      {darfZeugnis && (
        <>
          <div className={teil}>
            <div className={titel}>Vorhandenes zuordnen</div>
            <input className={feld} value={suche} placeholder="Nummer, Schmelze, Werkstoff" aria-label="Zeugnis suchen" onChange={(e) => setSuche(e.target.value)} />
            <div className="mt-1 max-h-36 overflow-auto">
              {kandidaten.length === 0 && (
                <p className="px-1.5 py-1 text-muted-foreground">
                  {suche.trim() ? 'kein passendes Zeugnis' : 'Nummer, Schmelze oder Werkstoff eingeben'}
                </p>
              )}
              {kandidaten.map((c) => {
                const schwaecher = z.certificateType && MATERIAL_CERTIFICATE_TYPES.indexOf(c.type) < MATERIAL_CERTIFICATE_TYPES.indexOf(z.certificateType)
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={laeuft}
                    className="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left hover:bg-muted"
                    onClick={() => void tu(() => assignCertificate(c.id, [z.id]))}
                  >
                    <span>
                      <b>{c.number}</b> · {MATERIAL_CERTIFICATE_TYPE_LABELS[c.type].replace('EN 10204 ', '')}
                      <span className="text-muted-foreground"> · {[c.heatNumber && `Schmelze ${c.heatNumber}`, c.material, c.issuer].filter(Boolean).join(' · ')}</span>
                    </span>
                    {schwaecher && <span className="text-[11px] text-amber-700 dark:text-amber-400">schwächer</span>}
                  </button>
                )
              })}
            </div>
          </div>

          <div className={teil}>
            <div className={titel}>Neues anlegen und zuordnen</div>
            <div className="grid grid-cols-2 gap-1.5">
              <label className="col-span-2">
                <span className="block text-[11px] text-muted-foreground">Datei (PDF) *</span>
                <input type="file" accept="application/pdf" className={feld} onChange={(e) => setNeu((n) => ({ ...n, datei: e.target.files?.[0] ?? null }))} />
              </label>
              <label>
                <span className="block text-[11px] text-muted-foreground">Nummer *</span>
                <input className={feld} value={neu.number} placeholder="lt. Zeugnis" onChange={(e) => setNeu((n) => ({ ...n, number: e.target.value }))} />
              </label>
              <label>
                <span className="block text-[11px] text-muted-foreground">Art</span>
                <select className={feld} value={neu.type} onChange={(e) => setNeu((n) => ({ ...n, type: e.target.value }))}>
                  {MATERIAL_CERTIFICATE_TYPES.map((c) => (
                    <option key={c} value={c}>
                      {MATERIAL_CERTIFICATE_TYPE_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="block text-[11px] text-muted-foreground">Schmelze</span>
                <input className={feld} value={neu.heatNumber} onChange={(e) => setNeu((n) => ({ ...n, heatNumber: e.target.value }))} />
              </label>
              <label>
                <span className="block text-[11px] text-muted-foreground">Werkstoff</span>
                <input className={feld} value={neu.material} onChange={(e) => setNeu((n) => ({ ...n, material: e.target.value }))} />
              </label>
              <label>
                <span className="block text-[11px] text-muted-foreground">Aussteller *</span>
                <input className={feld} value={neu.issuer} onChange={(e) => setNeu((n) => ({ ...n, issuer: e.target.value }))} />
              </label>
              <label>
                <span className="block text-[11px] text-muted-foreground">ausgestellt am</span>
                <input type="date" className={feld} value={neu.issuedAt} onChange={(e) => setNeu((n) => ({ ...n, issuedAt: e.target.value }))} />
              </label>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                disabled={laeuft || !neu.datei || !neu.number.trim() || !neu.issuer.trim() || !neu.issuedAt}
                className="rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50"
                onClick={() => void anlegen()}
              >
                Anlegen und zuordnen
              </button>
            </div>
          </div>
        </>
      )}

      {(z.inquiryDocument || z.orderDocument || z.receivedAt) && (
        <div className={teil}>
          <div className={titel}>Belege</div>
          {z.inquiryDocument && (
            <button type="button" className="block text-primary hover:underline" onClick={() => onOpenDocument?.(z.inquiryDocument!.id)}>
              Preisanfrage {z.inquiryDocument.number}
            </button>
          )}
          {z.orderDocument && (
            <button type="button" className="block text-primary hover:underline" onClick={() => onOpenDocument?.(z.orderDocument!.id)}>
              Bestellung {z.orderDocument.number} · bestellt {zahl(z.orderedQuantity)}
            </button>
          )}
          {z.receivedAt && <span className="block">erhalten am {alsTag(z.receivedAt)}</span>}
        </div>
      )}

      {fehler && <p className="mt-2 text-destructive">{fehler}</p>}
      {hinweis && <p className="mt-2 text-amber-700 dark:text-amber-400">{hinweis}</p>}
    </div>
  )
}
